import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import AppSidebar from "@/components/AppSidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import OutreachPanel from "@/components/OutreachPanel";
import {
  Search, Loader2, Plus, Download, ExternalLink, Globe, Instagram, Facebook, Linkedin,
  MapPin, Phone, Star, TrendingUp, ShieldCheck, ShieldAlert, RefreshCw, ArrowRightLeft, Filter as FilterIcon,
  X, Sparkles, Building2, Check, RotateCcw, Save, XCircle, Trash2, Send, Mail,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const tokenHeader = () => {
  const t = localStorage.getItem("token") || localStorage.getItem("heyintake_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const PHASE_OPTIONS = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "qualified", label: "Qualified" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
];

// Slice 2 — tone palette for the lead_phase pill (list view + drawer). Each
// phase gets a distinct color band so the workflow state reads at a glance.
const PHASE_TONE = {
  new:       "bg-zinc-100 text-zinc-700 border-zinc-200",
  contacted: "bg-blue-50 text-blue-700 border-blue-200",
  qualified: "bg-emerald-50 text-emerald-700 border-emerald-200",
  won:       "bg-yellow-50 text-yellow-800 border-yellow-300",
  lost:      "bg-rose-50 text-rose-700 border-rose-200",
};

const IDENTITY_TONE = {
  high:             { bg: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Identity · High" },
  medium:           { bg: "bg-amber-50 text-amber-700 border-amber-200",       label: "Identity · Medium" },
  low:              { bg: "bg-orange-50 text-orange-700 border-orange-200",    label: "Identity · Low" },
  unable_to_verify: { bg: "bg-rose-50 text-rose-700 border-rose-200",          label: "Identity · Unable" },
};

const RELEVANCE_TONE = {
  high:    "bg-emerald-50 text-emerald-700 border-emerald-200",
  medium:  "bg-amber-50 text-amber-700 border-amber-200",
  low:     "bg-rose-50 text-rose-700 border-rose-200",
  unknown: "bg-zinc-100 text-zinc-500 border-zinc-200",
};

const SOURCE_TONE = {
  website:        "bg-blue-50 text-blue-700 border-blue-200",
  social:         "bg-violet-50 text-violet-700 border-violet-200",
  gpt:            "bg-zinc-100 text-zinc-600 border-zinc-200",
  google_places:  "bg-emerald-50 text-emerald-700 border-emerald-200",
  manual:         "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const VERIFICATION_TONE = {
  high:             { bg: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Verified · High" },
  medium:           { bg: "bg-amber-50 text-amber-700 border-amber-200",       label: "Verified · Medium" },
  low:              { bg: "bg-orange-50 text-orange-700 border-orange-200",    label: "Needs review · Low" },
  unable_to_verify: { bg: "bg-zinc-100 text-zinc-600 border-zinc-200",         label: "Unable to verify" },
};

function ScoreBar({ value, color = "bg-indigo-500" }) {
  const v = Math.max(0, Math.min(100, value || 0));
  return (
    <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
      <div className={`h-full ${color}`} style={{ width: `${v}%` }} />
    </div>
  );
}

function ScoreLine({ row }) {
  const notChecked = row.not_checked === true;
  const sign = row.points > 0 ? "+" : row.points < 0 ? "" : " ";
  const tone = notChecked
    ? "text-zinc-400"
    : row.points > 0
      ? "text-emerald-700"
      : row.points < 0
        ? "text-rose-700"
        : "text-zinc-400";
  return (
    <div
      className="flex items-center justify-between gap-2 py-1 text-xs"
      data-testid={`score-line-${row.category?.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className={`tabular-nums font-semibold w-8 text-right ${tone}`}>
          {notChecked ? "—" : `${sign}${row.points}`}
        </span>
        <span className={`truncate ${notChecked ? "text-zinc-500" : "text-zinc-700"}`}>
          {row.category}
          {notChecked && (
            <span className="ml-1.5 text-[9px] uppercase tracking-wider px-1 py-0.5 rounded border border-zinc-200 bg-zinc-50 text-zinc-500">
              not checked
            </span>
          )}
        </span>
      </div>
      <span className="text-zinc-500 text-[11px] truncate text-right max-w-[55%]" title={row.reason || ""}>
        {row.reason || "—"}
      </span>
    </div>
  );
}

function ScorePanel({ title, total, color, breakdown = [], penalties = [], testid }) {
  return (
    <Card className="rounded-xl" data-testid={testid}>
      <CardContent className="p-3">
        <div className="flex items-baseline justify-between mb-1">
          <div className="text-xs font-medium text-zinc-700 uppercase tracking-wider">{title}</div>
          <div className="text-2xl font-semibold tabular-nums">
            {total ?? "—"}<span className="text-sm text-zinc-400"> / 100</span>
          </div>
        </div>
        <ScoreBar value={total} color={color} />
        <div className="mt-2 divide-y divide-zinc-100">
          {breakdown.map((row, i) => <ScoreLine key={`b-${i}`} row={row} />)}
          {penalties.map((row, i) => (
            <ScoreLine key={`p-${i}`} row={{ ...row, max: 0 }} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ScoreBreakdownPanel({ lead }) {
  if (!lead) return null;
  return (
    <div className="space-y-3" data-testid="score-breakdown-panel">
      <AIDPanel lead={lead} />
      <ScorePanel
        title="Opportunity"
        total={lead.opportunity_score}
        color="bg-indigo-500"
        breakdown={lead.opportunity_breakdown || []}
        penalties={lead.opportunity_penalties || []}
        testid="opportunity-panel"
      />
      <ScorePanel
        title="Service Gap"
        total={lead.service_gap_score}
        color="bg-amber-500"
        breakdown={lead.service_gap_breakdown || []}
        penalties={lead.service_gap_penalties || []}
        testid="service-gap-panel"
      />
      {(lead.aid_score_version || lead.score_version) && (
        <div className="text-[10px] text-zinc-400 text-right" data-testid="score-version">
          AI Discoverability {lead.aid_score_version || "v3"} · Opportunity/Service Gap {lead.score_version || "v2"}
        </div>
      )}
    </div>
  );
}


// ════════════════════ Slice 2: Recommendations Panel ════════════════════
function RecommendationsPanel({ leadId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [polishing, setPolishing] = useState(false);
  const [voice, setVoice] = useState("warm, professional, jargon-free");
  const [error, setError] = useState(null);

  const load = async () => {
    if (!leadId) return;
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${API}/lead-intel/leads/${leadId}/recommendations`, { headers: tokenHeader() });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load recommendations");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leadId]);

  const polish = async () => {
    if (!leadId) return;
    setPolishing(true);
    try {
      const r = await axios.post(
        `${API}/lead-intel/leads/${leadId}/recommendations/polish`,
        { voice },
        { headers: tokenHeader() },
      );
      setData(r.data);
      if (r.data.polished) toast.success(`Outreach polished in '${r.data.voice || voice}' voice`);
      else toast.warning(r.data.polish_error || "Polish skipped");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Polish failed");
    } finally { setPolishing(false); }
  };

  if (loading) return <Card className="rounded-xl"><CardContent className="p-3 text-xs text-zinc-500">Loading recommendations…</CardContent></Card>;
  if (error)   return <Card className="rounded-xl"><CardContent className="p-3 text-xs text-rose-700">{error}</CardContent></Card>;
  if (!data || (data.recommendations || []).length === 0) {
    return (
      <Card className="rounded-xl" data-testid="recommendations-panel-empty">
        <CardContent className="p-3 text-xs text-zinc-500">
          No actionable recommendations — this lead's signals are already strong.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-xl" data-testid="recommendations-panel">
      <CardContent className="p-3">
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-xs font-medium text-zinc-700 uppercase tracking-wider">
            Recommendations <span className="text-zinc-400">({data.recommendations.length})</span>
          </div>
          <div className="text-[10px] text-zinc-500">
            <span className="font-semibold text-amber-700">+{data.total_points_recoverable}</span> AID points recoverable
          </div>
        </div>
        {/* Voice + polish controls */}
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-zinc-100">
          <input
            type="text"
            value={voice}
            onChange={e => setVoice(e.target.value)}
            placeholder="Brand voice (e.g. warm, professional)"
            className="text-xs flex-1 px-2 py-1 border border-zinc-200 rounded-md"
            data-testid="rec-voice-input"
          />
          <button
            type="button"
            onClick={polish}
            disabled={polishing}
            data-testid="rec-polish-btn"
            className="text-xs px-3 py-1 rounded-md bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-50"
          >
            {polishing ? "Polishing…" : "Polish pitches"}
          </button>
        </div>
        <ul className="space-y-3">
          {data.recommendations.map((r, i) => (
            <li
              key={r.id}
              data-testid={`rec-item-${r.category.toLowerCase().replace(/\s+/g, "-")}`}
              className="border-l-2 pl-2"
              style={{
                borderColor: r.severity === "high" ? "#b45309" : r.severity === "medium" ? "#d97706" : "#a8a29e"
              }}
            >
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">{r.category}</span>
                <span className="font-mono tabular-nums font-semibold text-amber-700 text-xs">+{r.points_lost}</span>
                <span className={`text-[9px] uppercase tracking-wider px-1 py-0.5 rounded-full border ${
                  r.severity === "high" ? "bg-amber-100 text-amber-800 border-amber-300" :
                  r.severity === "medium" ? "bg-amber-50 text-amber-700 border-amber-200" :
                  "bg-zinc-100 text-zinc-600 border-zinc-200"
                }`}>{r.severity}</span>
                <span className="text-[10px] text-zinc-400 ml-auto">{r.source === "service_gap" ? "Service Gap" : "AI Discoverability"}</span>
              </div>
              <div className="text-[11px] text-zinc-400 italic mb-1.5">"{r.evidence}"</div>
              {/* Internal action */}
              <div className="mb-1.5">
                <div className="text-[9px] uppercase font-semibold text-zinc-500 mb-0.5">Internal action</div>
                <div className="text-[12px] text-zinc-800 leading-snug">{r.internal_action}</div>
              </div>
              {/* Outreach pitch (with polished version if available) */}
              {(r.outreach_pitch || r.outreach_pitch_polished) && (
                <div>
                  <div className="text-[9px] uppercase font-semibold text-zinc-500 mb-0.5 flex items-center gap-1.5">
                    Outreach pitch
                    {r.outreach_pitch_polished && (
                      <span className="text-[9px] px-1 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200">polished</span>
                    )}
                  </div>
                  <div className="text-[12px] text-zinc-700 leading-snug italic bg-zinc-50 rounded px-2 py-1.5 border border-zinc-100">
                    {r.outreach_pitch_polished || r.outreach_pitch}
                  </div>
                  {r.outreach_pitch_polished && (
                    <details className="mt-1">
                      <summary className="text-[10px] text-zinc-400 cursor-pointer hover:text-zinc-700">show original</summary>
                      <div className="text-[11px] text-zinc-500 mt-1 italic">{r.outreach_pitch}</div>
                    </details>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function AIDPanel({ lead }) {
  const total = lead.ai_discoverability_score;
  const breakdown = lead.ai_discoverability_breakdown || [];
  const strengths = lead.ai_discoverability_strengths || [];
  const opportunities = lead.ai_discoverability_opportunities || [];
  // "Why Not Higher?" — points earned vs max per category, deterministic.
  // Exclude NOT_CHECKED categories — those are pending evaluation, not gaps.
  const gaps = breakdown
    .filter(b => !b.not_checked)
    .map(b => ({
      category: b.category,
      points_lost: Math.max(0, (b.max || 0) - (b.points || 0)),
      max: b.max || 0,
      points: b.points || 0,
      reason: b.reason || "",
    }))
    .filter(g => g.points_lost > 0)
    .sort((a, b) => b.points_lost - a.points_lost);
  const earned = breakdown
    .filter(b => !b.not_checked && (b.points || 0) > 0)
    .map(b => ({ category: b.category, points: b.points, max: b.max, reason: b.reason || "" }));
  const pending = breakdown.filter(b => b.not_checked);
  // Normalised denominator (mirrors backend math)
  const checkedSum = breakdown
    .filter(b => !b.not_checked)
    .reduce((acc, b) => acc + (b.max || 0), 0);
  const totalCategories = breakdown.length;
  return (
    <Card className="rounded-xl" data-testid="aid-panel">
      <CardContent className="p-3">
        <div className="flex items-baseline justify-between mb-1">
          <div className="text-xs font-medium text-zinc-700 uppercase tracking-wider">AI Discoverability</div>
          <div className="text-2xl font-semibold tabular-nums">
            {total ?? "—"}<span className="text-sm text-zinc-400"> / 100</span>
          </div>
        </div>
        <ScoreBar value={total} color="bg-violet-500" />
        {pending.length > 0 && (
          <div className="mt-1 text-[10px] text-zinc-500" data-testid="aid-normalised-note">
            Normalised over {breakdown.length - pending.length} of {totalCategories} categories
            ({pending.length} pending evaluation)
          </div>
        )}
        <div className="mt-2 divide-y divide-zinc-100">
          {breakdown.map((row, i) => <ScoreLine key={`aid-${i}`} row={row} />)}
        </div>
        {gaps.length > 0 && (
          <div className="mt-3" data-testid="aid-why-not-higher">
            <div className="text-[10px] uppercase text-zinc-500 font-semibold mb-1.5 tracking-wider">
              Why not higher?
            </div>
            <ul className="space-y-1.5">
              {gaps.map((g, i) => (
                <li
                  key={`gap-${i}`}
                  className="flex items-start gap-2 text-[11px] leading-snug"
                  data-testid={`aid-gap-${g.category.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className="font-mono tabular-nums text-amber-700 font-semibold whitespace-nowrap pt-0.5">
                    +{g.points_lost}
                  </span>
                  <span className="text-zinc-700">
                    <span className="font-medium">{g.category}</span>
                    <span className="text-zinc-500"> · {g.points}/{g.max}</span>
                    {g.reason && <span className="text-zinc-500"> — {g.reason}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {pending.length > 0 && (
          <div className="mt-3" data-testid="aid-pending">
            <div className="text-[10px] uppercase text-zinc-500 font-semibold mb-1.5 tracking-wider">
              Pending evaluation
            </div>
            <ul className="space-y-1">
              {pending.map((p, i) => (
                <li
                  key={`pending-${i}`}
                  className="flex items-start gap-2 text-[11px] leading-snug text-zinc-600"
                  data-testid={`aid-pending-${p.category.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className="text-zinc-400">·</span>
                  <span>
                    <span className="font-medium">{p.category}</span>
                    {p.reason && <span className="text-zinc-500"> — {p.reason}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {earned.length > 0 && (
          <div className="mt-3" data-testid="aid-what-helped">
            <div className="text-[10px] uppercase text-emerald-700 font-semibold mb-1 tracking-wider">
              What's working
            </div>
            <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
              {earned.map((e, i) => (
                <div
                  key={`earned-${i}`}
                  className="text-[11px] text-emerald-800 truncate"
                  title={e.reason}
                  data-testid={`aid-earned-${e.category.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className="font-mono tabular-nums font-semibold mr-1">+{e.points}</span>
                  {e.category}
                </div>
              ))}
            </div>
          </div>
        )}
        {(strengths.length > 0 || opportunities.length > 0) && (
          <div className="mt-3 grid grid-cols-2 gap-3" data-testid="aid-strengths-opps">
            <div>
              <div className="text-[10px] uppercase text-emerald-700 font-semibold mb-1">Strengths</div>
              {strengths.length === 0
                ? <div className="text-[11px] text-zinc-400">None yet</div>
                : strengths.map((s, i) => (
                    <div key={`s-${i}`} className="text-[11px] text-emerald-700">
                      ✓ {s.category}
                    </div>
                  ))}
            </div>
            <div>
              <div className="text-[10px] uppercase text-amber-700 font-semibold mb-1">Suggestions</div>
              {opportunities.length === 0
                ? <div className="text-[11px] text-zinc-400">None</div>
                : opportunities.slice(0, 4).map((o, i) => (
                    <div key={`o-${i}`} className="text-[11px] text-zinc-700" title={o.suggestion}>
                      <span className="text-amber-700 font-semibold">+{o.points_lost}</span>{" "}{o.category}
                    </div>
                  ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function Kpi({ label, value, sub, icon: Icon, tone = "text-zinc-700" }) {
  return (
    <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
      <CardContent className="p-5">
        <div className="flex items-center gap-3 text-zinc-500 text-xs uppercase tracking-wider">
          {Icon && <Icon className={`w-4 h-4 ${tone}`} />}<span>{label}</span>
        </div>
        <div className="mt-2 text-2xl font-semibold text-zinc-900">{value}</div>
        {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}

export default function LeadIntelligencePage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Slice 3.0.5 — feature gate. Lead Intelligence is workspace-scoped and
  // can be enabled by a platform admin (entitlements.lead_intelligence).
  // Platform admins always pass through. Computed early — used at render
  // time below to short-circuit the page UI without breaking hook order.
  const hasLeadIntelAccess =
    user?.is_admin === true
    || (user?.entitlements && user.entitlements.lead_intelligence === true);

  const [discovering, setDiscovering] = useState(false);
  const [findingMore, setFindingMore] = useState(false);
  const [form, setForm] = useState({ category: "", city: "", state: "", country: "Canada", max_results: 10, run_name: "", discovery_mode: "places_only", include_rejected: false });
  const [filters, setFilters] = useState({});
  const [searchQ, setSearchQ] = useState("");
  // Slice 1.4 — sortable score columns. Defaults to Opportunity desc to
  // preserve existing behaviour; UI exposes AI Discoverability and
  // Service Gap as alternative sort keys.
  const [sortBy, setSortBy] = useState("opportunity_score");
  const [sortDir, setSortDir] = useState("desc");
  // Slice 1.5 — preset combined filter for "high-opportunity targets":
  // Opportunity ≥ 70, AI Discoverability ≤ 50, Service Gap ≥ 15. Toggling
  // it ON adds the three score thresholds; OFF removes only those three.
  const HIGH_OPP_PRESET = {
    min_opportunity_score: 70,
    max_ai_discoverability_score: 50,
    min_service_gap_score: 15,
  };
  const isHighOppActive =
    filters.min_opportunity_score === HIGH_OPP_PRESET.min_opportunity_score
    && filters.max_ai_discoverability_score === HIGH_OPP_PRESET.max_ai_discoverability_score
    && filters.min_service_gap_score === HIGH_OPP_PRESET.min_service_gap_score;
  const toggleHighOppPreset = () => {
    if (isHighOppActive) {
      setFilters(prev => {
        const next = { ...prev };
        delete next.min_opportunity_score;
        delete next.max_ai_discoverability_score;
        delete next.min_service_gap_score;
        return next;
      });
    } else {
      setFilters(prev => ({ ...prev, ...HIGH_OPP_PRESET }));
    }
  };
  useEffect(() => { fetchLeads(); /* eslint-disable-next-line */ }, [
    JSON.stringify(filters),
  ]);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [lastSummary, setLastSummary] = useState(null);
  const [showManual, setShowManual] = useState(false);
  // Primary workflow buckets: pending (default) | saved | rejected
  const [bucket, setBucket] = useState("pending");
  // Inside Pending, optional filter: only show leads system-flagged as Needs Review
  const [onlyNeedsReview, setOnlyNeedsReview] = useState(false);
  const [bucketCounts, setBucketCounts] = useState({ pending: 0, saved: 0, rejected: 0, needs_review: 0 });
  const [rejectionReasons, setRejectionReasons] = useState([
    "Wrong category", "Bad match", "Duplicate", "Franchise/generic result",
    "Broken website/socials", "Not relevant", "Other",
  ]);
  // Reject modal state
  const [rejectModal, setRejectModal] = useState({ open: false, lead: null });
  // Discovery runs: list + active filter
  const [runs, setRuns] = useState([]);
  const [runsExpanded, setRunsExpanded] = useState(true);
  const [activeRunId, setActiveRunId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Bulk-action selection (set of lead ids ticked in the list)
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Capabilities — whether Google Places is actually active on this environment
  const [discoveryCaps, setDiscoveryCaps] = useState({ google_places_active: null, modes: {} });

  const fetchRuns = async () => {
    try {
      const r = await axios.get(`${API}/lead-intel/runs`, { headers: tokenHeader() });
      setRuns(r.data.runs || []);
    } catch { /* silent — admin-gated, fine to swallow */ }
  };

  const fetchLeads = async (extraParams = {}) => {
    setLoadingList(true);
    try {
      const params = { ...filters, ...extraParams };
      if (searchQ.trim()) params.search = searchQ.trim();
      // The 4 bucket tabs map 1:1 to the backend's `review_status` values.
      // The backend transparently includes legacy "pending without email"
      // rows in the pending_no_email bucket and excludes them from pending.
      params.review_status = bucket;
      params.sort_by = sortBy;
      params.sort_dir = sortDir;
      if (bucket === "pending" && onlyNeedsReview) params.needs_review = true;
      if (activeRunId) params.discovery_run_id = activeRunId;
      const r = await axios.get(`${API}/lead-intel/leads`, { headers: tokenHeader(), params });
      setLeads(r.data.leads || []);
      setTotal(r.data.total || 0);
      if (r.data.bucket_counts) setBucketCounts(r.data.bucket_counts);
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Admin access required.");
      else toast.error(e?.response?.data?.detail || "Failed to load leads");
    } finally { setLoadingList(false); }
  };

  useEffect(() => { fetchLeads(); /* eslint-disable-next-line */ }, [bucket, onlyNeedsReview, activeRunId, sortBy, sortDir]);

  // Clear selection whenever the visible lead set might change semantically
  useEffect(() => { setSelectedIds(new Set()); }, [bucket, activeRunId, onlyNeedsReview]);

  // Load reasons + runs once
  useEffect(() => {
    axios.get(`${API}/lead-intel/rejection-reasons`, { headers: tokenHeader() })
      .then(r => { if (Array.isArray(r.data?.reasons) && r.data.reasons.length) setRejectionReasons(r.data.reasons); })
      .catch(() => {});
    axios.get(`${API}/lead-intel/discovery-capabilities`, { headers: tokenHeader() })
      .then(r => setDiscoveryCaps(r.data || { google_places_active: false, modes: {} }))
      .catch(() => setDiscoveryCaps({ google_places_active: false, modes: {} }));
    fetchRuns();
  }, []);

  const renameRun = async (runId, newName) => {
    try {
      const r = await axios.patch(`${API}/lead-intel/runs/${runId}`, { run_name: newName }, { headers: tokenHeader() });
      setRuns(prev => prev.map(x => x.id === runId ? { ...x, ...r.data } : x));
      toast.success("Run renamed");
    } catch { toast.error("Rename failed"); }
  };

  const archiveRun = async (runId, archived) => {
    try {
      const r = await axios.patch(
        `${API}/lead-intel/runs/${runId}`,
        { archived },
        { headers: tokenHeader() },
      );
      // Refresh — archived runs disappear from the dashboard list
      if (archived) {
        setRuns(prev => prev.filter(x => x.id !== runId));
        if (activeRunId === runId) setActiveRunId(null);
      } else {
        setRuns(prev => prev.map(x => x.id === runId ? { ...x, ...r.data } : x));
      }
      toast.success(archived ? "Run archived" : "Run restored");
    } catch { toast.error(archived ? "Archive failed" : "Restore failed"); }
  };

  const deleteRun = async (runId, cascade) => {
    try {
      await axios.delete(`${API}/lead-intel/runs/${runId}${cascade ? "?cascade=true" : ""}`, { headers: tokenHeader() });
      setRuns(prev => prev.filter(x => x.id !== runId));
      if (activeRunId === runId) setActiveRunId(null);
      toast.success(cascade ? "Run + leads deleted" : "Run deleted");
      if (cascade) await fetchLeads();
    } catch { toast.error("Delete failed"); }
  };

  // Optimistic updaters for save/reject/reset
  const applyLocalUpdate = (id, patch) => {
    setLeads(prev => prev.map(x => x.id === id ? { ...x, ...patch } : x));
    if (selected?.id === id) setSelected(prev => ({ ...prev, ...patch }));
  };
  const saveLead = async (lead) => {
    try {
      const r = await axios.post(`${API}/lead-intel/leads/${lead.id}/save`, {}, { headers: tokenHeader() });
      applyLocalUpdate(lead.id, r.data);
      toast.success(`Saved · ${lead.business_name}`);
      // If we're currently in Pending, the lead should drop out — refresh counts
      await Promise.all([fetchLeads(), fetchRuns()]);
    } catch { toast.error("Save failed"); }
  };
  const rejectLead = async (lead, reason, detail) => {
    try {
      const r = await axios.post(`${API}/lead-intel/leads/${lead.id}/reject`,
        { reason, detail }, { headers: tokenHeader() });
      applyLocalUpdate(lead.id, r.data);
      toast.success(`Rejected · ${lead.business_name}`);
      await Promise.all([fetchLeads(), fetchRuns()]);
    } catch { toast.error("Reject failed"); }
  };
  const resetReview = async (lead) => {
    try {
      const r = await axios.post(`${API}/lead-intel/leads/${lead.id}/reset-review`, {}, { headers: tokenHeader() });
      applyLocalUpdate(lead.id, r.data);
      toast.success(`Moved back to Pending`);
      await Promise.all([fetchLeads(), fetchRuns()]);
    } catch { toast.error("Could not reset"); }
  };

  // Re-crawl pipeline: re-fetch website, re-extract evidence, re-score
  const [recrawlingId, setRecrawlingId] = useState(null);
  const [recrawlingRunId, setRecrawlingRunId] = useState(null);
  const recrawlLead = async (lead) => {
    setRecrawlingId(lead.id);
    try {
      const r = await axios.post(`${API}/lead-intel/leads/${lead.id}/recrawl`, {}, { headers: tokenHeader() });
      const updated = r.data?.lead;
      const delta = r.data?.delta || {};
      if (updated) applyLocalUpdate(lead.id, updated);
      const d = delta.score_delta || 0;
      const sign = d > 0 ? "+" : "";
      const bits = [];
      ["email", "phone", "instagram", "facebook", "linkedin", "tiktok", "booking"].forEach(k => {
        if (delta[`gained_${k}`]) bits.push(`+${k}`);
      });
      const msg = `Re-crawled · ${lead.business_name}` +
        (bits.length ? ` · ${bits.join(" ")}` : "") +
        (d ? ` · OS ${sign}${d}` : "");
      toast.success(msg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-crawl failed");
    } finally {
      setRecrawlingId(null);
    }
  };
  const recrawlSelected = async () => {
    if (!selectedIds.size) return;
    if (!confirm(`Re-crawl ${selectedIds.size} selected leads? This re-fetches every website and recomputes scores.`)) return;
    setRecrawlingId("bulk");
    try {
      const r = await axios.post(`${API}/lead-intel/leads/bulk`,
        { action: "recrawl", lead_ids: Array.from(selectedIds) },
        { headers: tokenHeader() });
      const s = r.data?.summary || {};
      const healed = s.places_healed || 0;
      toast.success(
        `Re-crawled ${s.leads_processed}/${s.leads_scanned} · ` +
        (healed ? `healed ${healed} from Places · ` : "") +
        `+${s.gained_instagram} IG · +${s.gained_facebook} FB · +${s.gained_booking} booking · ` +
        `OS ${s.os_up}↑ ${s.os_down}↓`
      );
      await fetchLeads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk re-crawl failed");
    } finally {
      setRecrawlingId(null);
    }
  };
  const recrawlRun = async (runId, runName) => {
    if (!confirm(`Re-crawl every lead in "${runName || runId}"? This may take a few minutes for large runs.`)) return;
    setRecrawlingRunId(runId);
    try {
      const r = await axios.post(`${API}/lead-intel/runs/${runId}/recrawl`, {}, { headers: tokenHeader() });
      const s = r.data?.summary || {};
      const healed = s.places_healed || 0;
      toast.success(
        `Re-crawled ${s.leads_processed}/${s.leads_scanned} · ` +
        (healed ? `healed ${healed} from Places · ` : "") +
        `+${s.gained_instagram} IG · +${s.gained_facebook} FB · +${s.gained_booking} booking · ` +
        `OS ${s.os_up}↑ ${s.os_down}↓`
      );
      await Promise.all([fetchLeads(), fetchRuns()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run re-crawl failed");
    } finally {
      setRecrawlingRunId(null);
    }
  };

  // Bulk send outreach — opens a modal to pick template + sender, then
  // dispatches to the backend which handles suppression, daily-cap,
  // already-emailed dedup, and per-lead failure isolation.
  const [bulkSendOpen, setBulkSendOpen] = useState(false);
  const [bulkSendCtx, setBulkSendCtx] = useState(null); // {leadIds, templates, senders, capInfo, loading}

  const openBulkSend = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) { toast.error("Select at least one lead"); return; }
    setBulkSendCtx({ leadIds: ids, templates: [], senders: [], capInfo: null, loading: true });
    setBulkSendOpen(true);
    try {
      const [tr, sr, csr] = await Promise.all([
        axios.get(`${API}/workflow/templates?category=lead_intel_outreach`, { headers: tokenHeader() }),
        axios.get(`${API}/lead-intel/outreach/senders`, { headers: tokenHeader() }),
        axios.get(`${API}/lead-intel/leads/${ids[0]}/outreach/send-status`, { headers: tokenHeader() }),
      ]);
      setBulkSendCtx({
        leadIds: ids,
        templates: tr.data?.templates || [],
        senders: (sr.data?.senders || []).filter(s => s.active !== false),
        capInfo: {
          daily_cap: csr.data?.daily_cap ?? 0,
          sent_today: csr.data?.sent_today ?? 0,
          remaining: csr.data?.remaining ?? 0,
        },
        loading: false,
      });
    } catch (e) {
      setBulkSendOpen(false);
      setBulkSendCtx(null);
      toast.error(e?.response?.data?.detail || "Couldn't load bulk send options");
    }
  };

  const submitBulkSend = async ({ template_id, from_sender_id, include_already_emailed }) => {
    const ids = bulkSendCtx?.leadIds || [];
    if (!ids.length || !template_id) return;
    try {
      const r = await axios.post(`${API}/lead-intel/leads/outreach/bulk-send`, {
        lead_ids: ids,
        template_id,
        from_sender_id: from_sender_id || null,
        include_already_emailed: !!include_already_emailed,
        confirm: true,
      }, { headers: tokenHeader(), timeout: 600000 });
      const d = r.data || {};
      const s = d.skipped || {};
      const fr = d.failure_reasons || {};
      const parts = [`Sent ${d.sent}/${d.total}`];
      if (d.failed) {
        // Human-readable rollup: "18 failed (12 http_422, 6 exception)"
        const reasonBits = Object.entries(fr).map(([k, v]) => `${v} ${k}`).join(", ");
        parts.push(reasonBits ? `${d.failed} failed (${reasonBits})` : `${d.failed} failed`);
      }
      if (s.no_email) parts.push(`${s.no_email} no email`);
      if (s.suppressed) parts.push(`${s.suppressed} unsubscribed`);
      if (s.already_emailed) parts.push(`${s.already_emailed} already emailed`);
      if (s.invalid) parts.push(`${s.invalid} invalid`);
      if (d.over_cap?.length) parts.push(`${d.over_cap.length} over daily cap`);
      parts.push(`${d.remaining_after} sends left today`);
      const summary = parts.join(" · ");
      // Stash the first failure detail for the operator so they can see WHY.
      if (d.failed) {
        const firstFail = (d.results || []).find(rr => rr.status === "failed");
        toast.error(summary, {
          duration: 20000,
          description: firstFail?.detail ? `First failure: ${firstFail.detail}` : undefined,
        });
      } else {
        toast.success(summary, { duration: 15000 });
      }
      setBulkSendOpen(false);
      setBulkSendCtx(null);
      setSelectedIds(new Set());
      await fetchLeads();
      return d;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk send failed");
      throw e;
    }
  };

  // Slice 1.4 — Re-crawl all / stale leads in the background. Backend
  // returns a job_id; we poll progress and toast at completion.
  const [staleInfo, setStaleInfo] = useState({ total: 0, stale: 0, current_score_version: null });
  const [recrawlJob, setRecrawlJob] = useState(null); // {id,total,processed,...}

  const fetchStaleCount = async () => {
    try {
      const r = await axios.get(`${API}/lead-intel/stale-count`, { headers: tokenHeader() });
      setStaleInfo(r.data || { total: 0, stale: 0 });
    } catch { /* admin-gated */ }
  };
  useEffect(() => { fetchStaleCount(); /* eslint-disable-next-line */ }, []);

  const recrawlAll = async (scope) => {
    const n = scope === "stale" ? staleInfo.stale : staleInfo.total;
    if (!n) { toast.info("No leads to re-crawl"); return; }
    if (!confirm(
      scope === "stale"
        ? `Re-crawl ${n} leads with stale scores? They were scored on an older version of the AI Discoverability model. Runs in the background — you can keep using the page.`
        : `Re-crawl ALL ${n} leads in the background? This may take 5-15 minutes depending on website response times.`
    )) return;
    try {
      const r = await axios.post(
        `${API}/lead-intel/bulk-recrawl?scope=${scope}`,
        {},
        { headers: tokenHeader() },
      );
      if (!r.data.job_id) { toast.info(r.data.message || "Nothing to do"); return; }
      setRecrawlJob({
        id: r.data.job_id, total: r.data.total, processed: 0,
        succeeded: 0, failed: 0, status: "running", scope,
      });
      toast.success(`Started — re-crawling ${r.data.total} leads in the background`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start bulk re-crawl");
    }
  };

  // Poll the active job until it completes
  useEffect(() => {
    if (!recrawlJob?.id || recrawlJob.status !== "running") return;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/lead-intel/recrawl-jobs/${recrawlJob.id}`, { headers: tokenHeader() });
        setRecrawlJob(prev => ({ ...prev, ...r.data }));
        if (r.data.status !== "running") {
          clearInterval(t);
          const s = r.data.summary || {};
          if (r.data.status === "done") {
            toast.success(
              `Re-crawl complete · ${r.data.succeeded}/${r.data.total} succeeded` +
              (s.gained_instagram != null
                ? ` · +${s.gained_instagram} IG · +${s.gained_facebook} FB · +${s.gained_booking} booking`
                : "")
            );
          } else {
            toast.error(`Re-crawl ended with status: ${r.data.status}`);
          }
          await Promise.all([fetchLeads(), fetchStaleCount()]);
        }
      } catch { /* keep polling */ }
    }, 2500);
    return () => clearInterval(t);
  }, [recrawlJob?.id, recrawlJob?.status]);

  // Poll a running discovery run until finished_at is populated, then
  // refresh leads + show a final summary. Handles the async /discover
  // path where the backend returns immediately with status="running".
  const pollRunUntilComplete = async (runId, { onDone, onFail } = {}) => {
    const started = Date.now();
    const MAX_MS = 15 * 60 * 1000; // hard stop after 15 min
    let poll;
    const stop = () => { if (poll) clearInterval(poll); };
    poll = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/lead-intel/runs/${runId}`, { headers: tokenHeader() });
        const run = r.data || {};
        // Live progress: bump the visible KPIs / active-run leads while running
        await fetchRuns();
        if (run.finished_at) {
          stop();
          await fetchLeads();
          const inserted = run.inserted ?? 0;
          const requested = run.requested_results ?? 0;
          const note = run.coverage_note;
          const isError = note && /fail|abort|error/i.test(note);
          if (isError) {
            (onFail || toast.error)(note || "Discovery failed");
          } else {
            (onDone || toast.success)(`Inserted ${inserted}/${requested} new lead(s)${note ? ` · ${note}` : ""}`);
          }
        } else if (Date.now() - started > MAX_MS) {
          stop();
          toast.error("Discovery is taking longer than expected — check back in a minute.");
        }
      } catch (e) {
        // A transient poll error shouldn't kill the whole poll — retry.
        // Only bail if it's a 404 (run truly missing).
        if (e?.response?.status === 404) {
          stop();
          toast.error("Discovery run not found");
        }
      }
    }, 2500);
    return () => stop();
  };

  const runDiscovery = async () => {
    if (!form.category.trim() || !form.city.trim() || !form.country.trim()) {
      toast.error("Category, City, and Country are required"); return;
    }
    setDiscovering(true);
    setLastSummary(null);
    try {
      // Kicks off the async run; backend returns immediately (< 2s)
      // with a status="running" payload. We poll the run for progress.
      const r = await axios.post(`${API}/lead-intel/discover`, form, { headers: tokenHeader(), timeout: 30000 });
      setLastSummary(r.data);
      const runId = r.data.run_id;
      if (r.data.status === "running" && runId) {
        toast.info("Discovery started — you can leave this tab open and results will populate live.", { duration: 6000 });
        setActiveRunId(runId);
        setBucket("pending");
        await fetchRuns();
        pollRunUntilComplete(runId, {
          onDone: (msg) => toast.success(msg),
          onFail: (msg) => toast.error(msg),
        });
      } else {
        // Legacy sync path (should not fire post-refactor, kept for safety)
        toast.success(`Inserted ${r.data.inserted} new lead(s)`);
        await fetchRuns();
        if (runId) { setActiveRunId(runId); setBucket("pending"); }
        else { await fetchLeads(); }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Discovery failed");
    } finally { setDiscovering(false); }
  };

  const runFindMore = async () => {
    setFindingMore(true);
    try {
      const r = await axios.post(`${API}/lead-intel/find-more`, form, { headers: tokenHeader(), timeout: 30000 });
      setLastSummary(r.data);
      const runId = r.data.run_id;
      if (r.data.status === "running" && runId) {
        toast.info("Find-more started — results will populate live.", { duration: 6000 });
        setActiveRunId(runId);
        setBucket("pending");
        await fetchRuns();
        pollRunUntilComplete(runId, {
          onDone: (msg) => toast.success(msg),
          onFail: (msg) => toast.error(msg),
        });
      } else {
        // Legacy sync path
        const insertedTxt = `${r.data.inserted}/${r.data.requested} inserted`;
        const dupBreakdown = (r.data.duplicates_active || 0) + (r.data.duplicates_rejected || 0) > 0
          ? ` · ${r.data.duplicates_active || 0} active + ${r.data.duplicates_rejected || 0} rejected dup`
          : "";
        toast.success(`${insertedTxt}${dupBreakdown}`);
        await fetchRuns();
        if (runId) { setActiveRunId(runId); setBucket("pending"); }
        else { await fetchLeads(); }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Find-more failed");
    } finally { setFindingMore(false); }
  };

  const exportCsv = async () => {
    try {
      const params = { ...filters };
      if (searchQ.trim()) params.search = searchQ.trim();
      params.review_status = bucket;
      if (bucket === "pending" && onlyNeedsReview) params.needs_review = true;
      if (activeRunId) params.discovery_run_id = activeRunId;
      const r = await axios.get(`${API}/lead-intel/export`, { headers: tokenHeader(), params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "lead-intelligence.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  const kpiStats = useMemo(() => {
    const highOpp = leads.filter(l => (l.opportunity_score ?? 0) >= 70).length;
    const verified = leads.filter(l => {
      const c = l.verification_confidence || (l.verified ? "high" : "unable_to_verify");
      return c === "high" || c === "medium";
    }).length;
    const withBooking = leads.filter(l => l.booking_platform_name && l.booking_platform_name !== "Not Visible").length;
    return { high: highOpp, verified, withBooking };
  }, [leads]);

  // Feature gate render — Slice 3.0.5. Show after all hooks have been called.
  if (!hasLeadIntelAccess) {
    return (
      <div className="min-h-screen bg-zinc-50 flex" data-testid="lead-intel-locked">
        <AppSidebar user={user} onLogout={logout} />
        <main className="flex-1 ml-64 p-6">
          <div className="max-w-2xl mx-auto mt-16">
            <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.05)" }}>
              <CardContent className="p-10 text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-zinc-100 mb-5">
                  <Sparkles className="w-6 h-6 text-zinc-400" />
                </div>
                <h1 className="text-2xl font-semibold text-zinc-900 mb-2">Lead Intelligence is not enabled</h1>
                <p className="text-zinc-500 text-sm leading-relaxed max-w-md mx-auto">
                  This workspace doesn&apos;t have access to the Lead Intelligence module yet.
                  Contact your administrator to request access.
                </p>
                <div className="mt-6">
                  <Button variant="outline" size="sm" onClick={() => navigate("/dashboard")} data-testid="lead-intel-locked-back">
                    Back to dashboard
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex" data-testid="lead-intel-page">
      <AppSidebar user={user} onLogout={logout} />
      <main className="flex-1 ml-64 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-indigo-600 font-medium flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> Admin Module
            </div>
            <h1 className="text-3xl font-semibold text-zinc-900 mt-1">Lead Intelligence</h1>
            <p className="text-zinc-500 text-sm mt-1">Discover, enrich, and qualify businesses with AI.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowManual(true)} data-testid="add-manual-btn">
              <Plus className="w-4 h-4 mr-1" /> Add manual
            </Button>
            <Button variant="outline" size="sm" onClick={exportCsv} data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Discovery panel */}
        <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
          <CardHeader><CardTitle className="text-base">Discover new leads</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div>
                <Label className="text-xs">Category</Label>
                <Input placeholder="Hair Salon" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} data-testid="discover-category" />
              </div>
              <div>
                <Label className="text-xs">City</Label>
                <Input placeholder="Calgary" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} data-testid="discover-city" />
              </div>
              <div>
                <Label className="text-xs">State / Province</Label>
                <Input placeholder="Alberta" value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} data-testid="discover-state" />
              </div>
              <div>
                <Label className="text-xs">Country</Label>
                <Input placeholder="Canada" value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} data-testid="discover-country" />
              </div>
              <div>
                <Label className="text-xs">Max results</Label>
                <Input type="number" min={1} max={100} value={form.max_results} onChange={e => setForm({ ...form, max_results: Number(e.target.value) || 10 })} data-testid="discover-max" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Run name <span className="text-zinc-400 font-normal">(optional — helps you find this search later)</span></Label>
              <Input
                placeholder="e.g. Photographers Q1 — Toronto"
                value={form.run_name}
                onChange={e => setForm({ ...form, run_name: e.target.value })}
                data-testid="discover-run-name"
              />
            </div>
            <div className="flex items-start gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2">
              <input
                type="checkbox"
                id="discover-include-rejected"
                checked={!!form.include_rejected}
                onChange={e => setForm({ ...form, include_rejected: e.target.checked })}
                className="mt-0.5"
                data-testid="discover-include-rejected"
              />
              <Label htmlFor="discover-include-rejected" className="text-xs font-normal cursor-pointer">
                Allow re-discovery of rejected leads
                <div className="text-[10px] text-zinc-500 mt-0.5">
                  By default, leads you've rejected are skipped so Google Places budget isn't wasted on re-evaluating them. Enable this only if you want to give rejected businesses a second look.
                </div>
              </Label>
            </div>
            <div>
              <Label className="text-xs">Discovery mode</Label>
              {discoveryCaps.google_places_active === false && (
                <div className="mt-1 mb-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="places-key-missing-banner">
                  ⚠ <b>Google Places is not configured</b> on this environment.
                  Places-based modes are disabled. Add <code>GOOGLE_PLACES_API_KEY</code> to your environment to enable them.
                </div>
              )}
              {discoveryCaps.google_places_active === true && (
                <div className="mt-1 mb-2 text-[10px] text-emerald-700" data-testid="places-key-ok-banner">
                  ✓ Google Places API active on this environment.
                </div>
              )}
              <div className="flex flex-wrap gap-2 mt-2" data-testid="discovery-mode-picker">
                {[
                  { key: "places_only",     label: "Google Places only",   hint: "Most accurate · default", needs_places: true },
                  { key: "places_plus_gpt", label: "Places + GPT top-up",  hint: "Best coverage",           needs_places: true },
                  { key: "gpt_only",        label: "GPT only",              hint: "Legacy mode",            needs_places: false },
                ].map(opt => {
                  const active = form.discovery_mode === opt.key;
                  const disabled = opt.needs_places && discoveryCaps.google_places_active === false;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      disabled={disabled}
                      onClick={() => !disabled && setForm({ ...form, discovery_mode: opt.key })}
                      data-testid={`discovery-mode-${opt.key}`}
                      className={`text-xs px-3 py-2 rounded-lg border text-left transition-colors flex-1 min-w-[180px] ${
                        disabled
                          ? "bg-zinc-50 border-zinc-200 text-zinc-400 cursor-not-allowed"
                          : active
                            ? "bg-indigo-50 border-indigo-300 text-indigo-800"
                            : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-300"
                      }`}
                    >
                      <div className="font-medium">{opt.label}</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">
                        {disabled ? "Unavailable — API key missing" : opt.hint}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={runDiscovery} disabled={discovering} data-testid="discover-btn">
                {discovering ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Discovering…</> : <><Search className="w-4 h-4 mr-2" /> Discover</>}
              </Button>
              <Button variant="outline" onClick={runFindMore} disabled={findingMore || discovering} data-testid="find-more-btn">
                {findingMore ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Searching…</> : <><RefreshCw className="w-4 h-4 mr-2" /> Find more</>}
              </Button>
              {lastSummary && (
                <div className="ml-auto text-sm flex items-center gap-3 text-zinc-600" data-testid="last-summary">
                  <span>
                    {lastSummary.inserted}/{lastSummary.requested} inserted ·{" "}
                    {lastSummary.duplicates_skipped} dup
                    {(lastSummary.duplicates_active > 0 || lastSummary.duplicates_rejected > 0) && (
                      <span className="text-zinc-400 text-xs"> ({lastSummary.duplicates_active}a · {lastSummary.duplicates_rejected}r)</span>
                    )}
                    {" · "}{lastSummary.candidates_checked || lastSummary.discovered || 0} checked
                    {" · "}{lastSummary.elapsed_seconds}s
                  </span>
                </div>
              )}
            </div>
            {lastSummary?.exhaustion_reason && lastSummary.inserted < lastSummary.requested && (
              <div className="text-xs text-zinc-600 mt-1" data-testid="exhaustion-reason">
                Stopped because: <span className="font-medium">{
                  {
                    "hit_max_results": "requested count reached",
                    "source_exhausted": "Google Places returned no more unique leads for this query",
                    "safety_cap": "per-run safety cap reached (5× requested)",
                    "provider_error": "provider error — see coverage note",
                  }[lastSummary.exhaustion_reason] || lastSummary.exhaustion_reason
                }</span>
                {lastSummary.top_up_iterations > 1 && (
                  <span className="text-zinc-400"> · {lastSummary.top_up_iterations} top-up iterations</span>
                )}
              </div>
            )}
            {lastSummary?.coverage_note && (
              <div className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-2" data-testid="coverage-note">
                {lastSummary.coverage_note}
              </div>
            )}
          </CardContent>
        </Card>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label="Total leads" value={total} icon={Building2} tone="text-indigo-500" />
          <Kpi label="High fit" value={kpiStats.high} icon={TrendingUp} tone="text-emerald-500" sub={`${total ? Math.round((kpiStats.high / total) * 100) : 0}% of total`} />
          <Kpi label="Verified" value={kpiStats.verified} icon={ShieldCheck} tone="text-blue-500" />
          <Kpi label="With booking platform" value={kpiStats.withBooking} icon={Sparkles} tone="text-violet-500" />
        </div>

        {/* Filters bar */}
        <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
          <CardContent className="p-4 flex gap-2 flex-wrap items-center">
            <FilterIcon className="w-4 h-4 text-zinc-400" />
            <Input placeholder="Search business name, website, IG handle…" value={searchQ}
                   onChange={e => setSearchQ(e.target.value)}
                   onKeyDown={e => e.key === "Enter" && fetchLeads()}
                   className="w-72" data-testid="search-input" />
            {/* Phase filter is now the chip group below; keep Source / Verification here */}
            <Select value={filters.lead_source || "all"}
                    onValueChange={v => setFilters({ ...filters, lead_source: v === "all" ? undefined : v })}>
              <SelectTrigger className="w-40"><SelectValue placeholder="Source" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any source</SelectItem>
                <SelectItem value="gpt_search">GPT / Web Search</SelectItem>
                <SelectItem value="google_places">Google Places</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="csv_import">CSV Import</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.verification_confidence || "all"}
                    onValueChange={v => setFilters({ ...filters, verification_confidence: v === "all" ? undefined : v })}>
              <SelectTrigger className="w-44" data-testid="filter-verification"><SelectValue placeholder="Verification" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any confidence</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="unable_to_verify">Unable to verify</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" onClick={() => fetchLeads()}>Apply</Button>
            <div className="flex items-center gap-1.5 ml-auto">
              <span className="text-[11px] text-zinc-500 uppercase tracking-wider">Sort</span>
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className="h-8 text-xs border border-zinc-200 rounded-md px-2 bg-white"
                data-testid="leads-sort-by"
              >
                <option value="opportunity_score">Opportunity</option>
                <option value="ai_discoverability_score">AI Discoverability</option>
                <option value="service_gap_score">Service Gap</option>
                <option value="google_review_count">Review count</option>
                <option value="google_rating">Google rating</option>
                <option value="created_at">Newest</option>
              </select>
              <button
                type="button"
                onClick={() => setSortDir(sortDir === "desc" ? "asc" : "desc")}
                className="h-8 text-xs border border-zinc-200 rounded-md px-2 bg-white hover:bg-zinc-50"
                data-testid="leads-sort-dir"
                title={`Currently sorting ${sortDir === "desc" ? "descending" : "ascending"} — click to flip`}
              >
                {sortDir === "desc" ? "↓" : "↑"}
              </button>
            </div>
            {(Object.keys(filters).length > 0 || searchQ) && (
              <Button size="sm" variant="ghost" onClick={() => { setFilters({}); setSearchQ(""); setTimeout(fetchLeads, 0); }}>
                <X className="w-3 h-3 mr-1" /> Clear
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Discovery Runs panel */}
        <DiscoveryRunsPanel
          runs={runs}
          expanded={runsExpanded}
          setExpanded={setRunsExpanded}
          activeRunId={activeRunId}
          setActiveRunId={setActiveRunId}
          onRename={renameRun}
          onDelete={deleteRun}
          onArchive={archiveRun}
          onRecrawl={recrawlRun}
          recrawlingRunId={recrawlingRunId}
          onOpenHistory={() => setHistoryOpen(true)}
        />

        {/* Run History sheet */}
        <RunHistorySheet
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          tokenHeader={tokenHeader}
          API={API}
          onRename={renameRun}
          onDelete={deleteRun}
          onArchive={archiveRun}
          onRecrawl={recrawlRun}
          recrawlingRunId={recrawlingRunId}
          activeRunId={activeRunId}
          setActiveRunId={(id) => { setActiveRunId(id); setHistoryOpen(false); }}
        />

        {/* Slice 1.4 — Stale-score banner + Re-crawl All controls */}
        {(staleInfo.stale > 0 || recrawlJob) && (
          <div
            className={`rounded-lg border px-3 py-2 flex items-center gap-3 flex-wrap ${
              recrawlJob?.status === "running"
                ? "border-indigo-200 bg-indigo-50/50"
                : "border-amber-200 bg-amber-50/40"
            }`}
            data-testid="recrawl-banner"
          >
            {recrawlJob?.status === "running" ? (
              <>
                <div className="text-sm text-indigo-900 font-medium flex-1 min-w-[260px]" data-testid="recrawl-job-progress">
                  Re-crawling {recrawlJob.scope === "stale" ? "stale" : "all"} leads ·
                  <span className="ml-1 font-semibold tabular-nums">
                    {recrawlJob.processed} / {recrawlJob.total}
                  </span>
                  <span className="text-indigo-700 ml-2 text-xs">
                    ({recrawlJob.succeeded} ok{recrawlJob.failed ? `, ${recrawlJob.failed} failed` : ""})
                  </span>
                </div>
                <div className="flex-1 h-2 bg-indigo-100 rounded-full overflow-hidden min-w-[150px]">
                  <div
                    className="h-full bg-indigo-500 transition-all"
                    style={{ width: `${Math.round(100 * (recrawlJob.processed || 0) / Math.max(1, recrawlJob.total))}%` }}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="text-sm text-amber-900 flex-1 min-w-[280px]">
                  <span className="font-medium">{staleInfo.stale} of {staleInfo.total} leads</span> have
                  scores from an older version. Re-crawl to refresh schema, social, identity, and contact-form evidence.
                </div>
                <button
                  type="button"
                  onClick={() => recrawlAll("stale")}
                  className="text-xs px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white font-medium"
                  data-testid="recrawl-stale-btn"
                >
                  Re-crawl {staleInfo.stale} stale
                </button>
                <button
                  type="button"
                  onClick={() => recrawlAll("all")}
                  className="text-xs px-3 py-1.5 rounded-md bg-white hover:bg-zinc-50 border border-zinc-300 text-zinc-700"
                  data-testid="recrawl-all-btn"
                >
                  Re-crawl all {staleInfo.total}
                </button>
              </>
            )}
          </div>
        )}

        {/* Workflow bucket tabs: Pending Review / Pending — No Email / Saved / Rejected */}
        <div className="flex items-center gap-2 border-b border-zinc-200 flex-wrap" data-testid="lead-intel-buckets">
          {[
            { key: "pending",          label: "Pending Review",   count: bucketCounts.pending,          tone: "indigo" },
            { key: "pending_no_email", label: "Pending — No Email", count: bucketCounts.pending_no_email, tone: "amber" },
            { key: "saved",            label: "Saved",            count: bucketCounts.saved,            tone: "emerald" },
            { key: "rejected",         label: "Rejected",         count: bucketCounts.rejected,         tone: "rose" },
          ].map(t => {
            const active = bucket === t.key;
            const activeBorder = { indigo: "border-indigo-600 text-indigo-700",
                                   amber: "border-amber-600 text-amber-700",
                                   emerald: "border-emerald-600 text-emerald-700",
                                   rose: "border-rose-600 text-rose-700" }[t.tone];
            const pillBg = { indigo: "bg-indigo-100 text-indigo-700",
                             amber: "bg-amber-100 text-amber-700",
                             emerald: "bg-emerald-100 text-emerald-700",
                             rose: "bg-rose-100 text-rose-700" }[t.tone];
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setBucket(t.key)}
                data-testid={`bucket-${t.key}`}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                  active ? activeBorder : "border-transparent text-zinc-500 hover:text-zinc-800"
                }`}
              >
                {t.label}
                <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${active ? pillBg : "bg-zinc-100 text-zinc-600"}`}>
                  {t.count ?? 0}
                </span>
              </button>
            );
          })}
          {/* Right-side filter cluster — present in every bucket */}
          <div className="ml-auto flex items-center gap-2 pb-1 flex-wrap">
            {/* Slice 2 — Lead Phase filter chip group */}
            <div className="flex items-center gap-1" data-testid="phase-filter-group">
              <span className="text-[10px] uppercase tracking-wider text-zinc-400 mr-1">Phase</span>
              {[{value: undefined, label: "All"}, ...PHASE_OPTIONS].map(p => {
                const active = filters.lead_phase === p.value;
                const tone = p.value ? PHASE_TONE[p.value] : "bg-white border-zinc-200 text-zinc-700";
                return (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => setFilters({ ...filters, lead_phase: p.value })}
                    data-testid={`phase-filter-${p.value || "all"}`}
                    className={`text-[11px] px-2 py-1 rounded-full border transition-colors capitalize ${
                      active ? `${tone} ring-2 ring-offset-1 ring-indigo-300 font-semibold` : `${tone} opacity-60 hover:opacity-100`
                    }`}
                  >{p.label}</button>
                );
              })}
            </div>
            {/* Slice 1.5 — High-opportunity preset (Opp ≥70 · AID ≤50 · Gap ≥15) */}
            <button
              type="button"
              onClick={toggleHighOppPreset}
              data-testid="toggle-high-opp-preset"
              title="High Opportunity (≥70) · Low AI Discoverability (≤50) · High Service Gap (≥15)"
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1.5 ${
                isHighOppActive
                  ? "bg-violet-50 border-violet-300 text-violet-800"
                  : "bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300"
              }`}
            >
              {isHighOppActive ? "✓ " : ""}High-opp targets
              <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded-full bg-zinc-50 border border-zinc-200 text-zinc-500 font-mono">
                Opp≥70 · AID≤50 · Gap≥15
              </span>
            </button>
            {/* Inside Pending, expose the Needs-review quality filter */}
            {bucket === "pending" && (
              <button
                type="button"
                onClick={() => setOnlyNeedsReview(v => !v)}
                data-testid="toggle-needs-review-filter"
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  onlyNeedsReview
                    ? "bg-orange-50 border-orange-300 text-orange-800"
                    : "bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300"
                }`}
              >
                {onlyNeedsReview ? "✓ " : ""}Show only Needs review
                <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full ${onlyNeedsReview ? "bg-orange-200" : "bg-zinc-100"}`}>
                  {bucketCounts.needs_review ?? 0}
                </span>
              </button>
            )}
            {/* Backfill action on the No-Email tab — re-crawls every lead
                here with the latest extractor (JS-deobfuscation, /contact
                subpage scan) so historical misses get a second chance. */}
            {bucket === "pending_no_email" && (
              <button
                type="button"
                disabled={!!recrawlingId}
                onClick={async () => {
                  if (!window.confirm(
                    `Re-crawl all ${bucketCounts.pending_no_email || 0} leads in this tab with the latest extractor?\n\n` +
                    `Leads with newly-found emails will move to Pending Review automatically.\n\n` +
                    `This can take up to a minute.`
                  )) return;
                  setRecrawlingId("backfill");
                  try {
                    const r = await axios.post(
                      `${API}/lead-intel/leads/backfill-no-email-recrawl`, {},
                      { headers: tokenHeader() }
                    );
                    const { processed, found_email, promoted_to_pending } = r.data;
                    toast.success(
                      `Re-crawled ${processed} leads · found email on ${found_email} · ${promoted_to_pending} promoted to Pending Review.`
                    );
                    await fetchLeads();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Backfill failed");
                  } finally {
                    setRecrawlingId(null);
                  }
                }}
                data-testid="backfill-no-email"
                title="Re-crawl every lead in this tab with the latest extractor (JS-deobfuscation, subpage scan)."
                className="text-xs px-3 py-1.5 rounded-full border bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-3 h-3 inline-block mr-1 ${recrawlingId === "backfill" ? "animate-spin" : ""}`} />
                {recrawlingId === "backfill" ? "Re-scanning…" : "Re-scan all for emails"}
              </button>
            )}
          </div>
        </div>

        {/* Bulk action bar */}
        <BulkActionBar
          leads={leads}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          bucket={bucket}
          bucketCounts={bucketCounts}
          activeRunId={activeRunId}
          filters={filters}
          searchQ={searchQ}
          onlyNeedsReview={onlyNeedsReview}
          rejectionReasons={rejectionReasons}
          tokenHeader={tokenHeader}
          API={API}
          onDone={() => { setSelectedIds(new Set()); fetchLeads(); fetchRuns(); }}
          onRecrawlSelected={recrawlSelected}
          onBulkSend={openBulkSend}
          recrawlingId={recrawlingId}
        />

        {/* List */}
        <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
          <CardContent className="p-0">
            {loadingList ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>
            ) : leads.length === 0 ? (
              <div className="px-6 py-16 text-center text-zinc-500">
                No leads yet. Use the Discover panel above to find businesses.
              </div>
            ) : (
              <ul className="divide-y divide-zinc-100" data-testid="leads-list">
                {leads.map(l => (
                  <li key={l.id}>
                    <div className="flex w-full">
                      <div className="flex items-start pl-6 pt-5">
                        <input
                          type="checkbox"
                          aria-label={`Select ${l.business_name}`}
                          data-testid={`lead-select-${l.id}`}
                          checked={selectedIds.has(l.id)}
                          onChange={(e) => {
                            const next = new Set(selectedIds);
                            if (e.target.checked) next.add(l.id);
                            else next.delete(l.id);
                            setSelectedIds(next);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 accent-indigo-600 cursor-pointer"
                        />
                      </div>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelected(l)}
                        onKeyDown={(e) => { if (e.key === "Enter") setSelected(l); }}
                        data-testid={`lead-row-${l.id}`}
                        className="flex-1 text-left px-6 py-4 hover:bg-zinc-50 transition-colors cursor-pointer"
                      >
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-medium text-zinc-900 truncate">{l.business_name}</h3>
                            {(() => {
                              const c = l.identity_confidence || l.verification_confidence || "unable_to_verify";
                              const tone = IDENTITY_TONE[c] || IDENTITY_TONE.unable_to_verify;
                              return (
                                <Badge variant="outline" className={`text-[10px] ${tone.bg}`} data-testid={`identity-${c}`}>
                                  {tone.label}
                                </Badge>
                              );
                            })()}
                            {l.is_franchise_chain && (
                              <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200">
                                Franchise/Chain
                              </Badge>
                            )}
                            {l.needs_review && (
                              <Badge variant="outline" className="text-[10px] bg-orange-50 text-orange-700 border-orange-200" data-testid="needs-review-chip">
                                Needs review
                              </Badge>
                            )}
                            {l.lead_source === "google_places" && (
                              <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="source-google-places">
                                Google verified
                              </Badge>
                            )}
                            {l.lead_source === "hybrid_topup" && (
                              <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                                GPT top-up
                              </Badge>
                            )}
                            {l.review_status === "saved" && (
                              <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Saved</Badge>
                            )}
                            {l.review_status === "rejected" && (
                              <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200">Rejected</Badge>
                            )}
                            {l.lead_phase && (
                              <Badge
                                variant="outline"
                                className={`text-[10px] capitalize ${PHASE_TONE[l.lead_phase] || PHASE_TONE.new}`}
                                data-testid={`lead-phase-badge-${l.id}`}
                              >
                                {l.lead_phase}
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-zinc-500 mt-1 flex items-center gap-3 flex-wrap">
                            {l.category && <span>{l.category}</span>}
                            {l.city && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{l.city}{l.state ? `, ${l.state}` : ""}</span>}
                            {l.website && <span className="inline-flex items-center gap-1"><Globe className="w-3 h-3" />{l.website.replace(/^https?:\/\/(www\.)?/, "").slice(0, 40)}</span>}
                            {typeof l.google_rating === "number" && (
                              <span className="inline-flex items-center gap-1 text-amber-700" data-testid={`row-google-rating-${l.id}`}>
                                <Star className="w-3 h-3 fill-amber-400 text-amber-500" />
                                {l.google_rating} {l.google_review_count != null && <span className="text-zinc-400">({l.google_review_count})</span>}
                              </span>
                            )}
                            {l.google_business_url && (
                              <a
                                href={l.google_business_url}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 text-indigo-600 hover:underline"
                                data-testid={`row-google-maps-${l.id}`}
                              >
                                <ExternalLink className="w-3 h-3" /> Maps
                              </a>
                            )}
                            {l.booking_platform_name && l.booking_platform_name !== "Not Visible" && (
                              <span className="text-violet-600">{l.booking_platform_name}</span>
                            )}
                            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                              l.lead_source === "google_places" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                              l.lead_source === "gpt_search" ? "bg-zinc-100 text-zinc-600 border-zinc-200" :
                              l.lead_source === "hybrid_topup" ? "bg-amber-50 text-amber-700 border-amber-200" :
                              "bg-zinc-100 text-zinc-500 border-zinc-200"
                            }`} data-testid={`row-source-${l.id}`}>
                              {l.lead_source === "google_places" ? "Google Places" :
                               l.lead_source === "hybrid_topup" ? "GPT Top-up" :
                               l.lead_source === "gpt_search" ? "GPT" :
                               (l.lead_source || "—")}
                            </span>
                          </div>
                          {l.review_status === "rejected" && l.rejection_reason && (
                            <div className="text-[11px] text-rose-700 mt-1" data-testid={`row-rejection-reason-${l.id}`}>
                              <span className="text-zinc-400">Reason:</span> {l.rejection_reason}
                            </div>
                          )}
                        </div>

                        <div className="flex items-start gap-3 flex-shrink-0">
                          {/* Per-row quick actions, contextual to current bucket */}
                          <div className="flex flex-col gap-1.5" onClick={(e) => e.stopPropagation()}>
                            {bucket === "pending" && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-[11px] border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                                  onClick={() => saveLead(l)}
                                  data-testid={`row-save-${l.id}`}
                                >
                                  <Check className="w-3 h-3 mr-1" /> Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-[11px] border-rose-200 text-rose-700 hover:bg-rose-50"
                                  onClick={() => setRejectModal({ open: true, lead: l })}
                                  data-testid={`row-reject-${l.id}`}
                                >
                                  <X className="w-3 h-3 mr-1" /> Reject
                                </Button>
                              </>
                            )}
                            {(bucket === "saved" || bucket === "rejected") && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-[11px] text-zinc-600 hover:bg-zinc-100"
                                onClick={() => resetReview(l)}
                                data-testid={`row-reset-${l.id}`}
                              >
                                <RotateCcw className="w-3 h-3 mr-1" /> Move to Pending
                              </Button>
                            )}
                          </div>

                          <div className="text-right space-y-2 min-w-[140px]" data-testid={`lead-scores-${l.id}`}>
                            <div>
                              <div className="text-[10px] text-zinc-400 uppercase tracking-wider">Opportunity</div>
                              <div className="flex items-center gap-1.5">
                                <div className="text-lg font-semibold text-zinc-900 tabular-nums" data-testid={`lead-opp-${l.id}`}>
                                  {l.opportunity_score ?? "—"}
                                </div>
                                <div className="flex-1"><ScoreBar value={l.opportunity_score} color="bg-indigo-500" /></div>
                              </div>
                            </div>
                            <div>
                              <div className="text-[10px] text-zinc-400 uppercase tracking-wider">AI Discoverability</div>
                              <div className="flex items-center gap-1.5">
                                <div className="text-sm font-semibold text-zinc-700 tabular-nums" data-testid={`lead-aid-${l.id}`}>
                                  {l.ai_discoverability_score ?? "—"}
                                </div>
                                <div className="flex-1"><ScoreBar value={l.ai_discoverability_score} color="bg-violet-500" /></div>
                              </div>
                            </div>
                            <div>
                              <div className="text-[10px] text-zinc-400 uppercase tracking-wider">Service Gap</div>
                              <div className="flex items-center gap-1.5">
                                <div className="text-sm font-semibold text-zinc-700 tabular-nums" data-testid={`lead-gap-${l.id}`}>
                                  {l.service_gap_score ?? "—"}
                                </div>
                                <div className="flex-1"><ScoreBar value={l.service_gap_score} color="bg-amber-500" /></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail drawer */}
      <LeadDetailDrawer
        lead={selected}
        onClose={() => setSelected(null)}
        onChanged={(updated) => {
          setLeads(prev => prev.map(x => x.id === updated.id ? updated : x));
          setSelected(updated);
        }}
        onDeleted={(id) => {
          setLeads(prev => prev.filter(x => x.id !== id));
          setSelected(null);
          setTotal(t => Math.max(0, t - 1));
        }}
        onSave={saveLead}
        onReject={(lead) => setRejectModal({ open: true, lead })}
        onReset={resetReview}
        onRecrawl={recrawlLead}
        recrawlingId={recrawlingId}
      />

      {/* Manual create */}
      <ManualCreateDialog
        open={showManual}
        onClose={() => setShowManual(false)}
        onCreated={(lead) => { setLeads(prev => [lead, ...prev]); setTotal(t => t + 1); }}
      />

      {/* Reject modal */}
      <RejectLeadDialog
        state={rejectModal}
        reasons={rejectionReasons}
        onClose={() => setRejectModal({ open: false, lead: null })}
        onConfirm={async (reason, detail) => {
          const lead = rejectModal.lead;
          setRejectModal({ open: false, lead: null });
          if (lead) await rejectLead(lead, reason, detail);
        }}
      />

      {/* Bulk send outreach modal */}
      <BulkSendModal
        open={bulkSendOpen}
        ctx={bulkSendCtx}
        onClose={() => { setBulkSendOpen(false); setBulkSendCtx(null); }}
        onSubmit={submitBulkSend}
      />
        </main>
    </div>
  );
}

function LeadDetailDrawer({ lead, onClose, onChanged, onDeleted, onSave, onReject, onReset, onRecrawl, recrawlingId }) {
  const [savingPhase, setSavingPhase] = useState(false);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => { setNotes(lead?.notes || ""); }, [lead?.id]);

  if (!lead) return null;

  const update = async (payload) => {
    const r = await axios.patch(`${API}/lead-intel/leads/${lead.id}`, payload, { headers: tokenHeader() });
    onChanged?.(r.data);
    return r.data;
  };

  const changePhase = async (p) => {
    setSavingPhase(true);
    try { await update({ lead_phase: p }); toast.success(`Moved to ${p}`); }
    catch (e) { toast.error("Failed to update"); }
    finally { setSavingPhase(false); }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try { await update({ notes }); toast.success("Notes saved"); }
    finally { setSavingNotes(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try { await axios.delete(`${API}/lead-intel/leads/${lead.id}`, { headers: tokenHeader() }); toast.success("Deleted"); onDeleted?.(lead.id); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <Sheet open={!!lead} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="lead-detail-drawer">
        <SheetHeader>
          <SheetTitle className="text-xl pr-8">{lead.business_name}</SheetTitle>
        </SheetHeader>

        <div className="space-y-4 mt-4 text-sm">
          <div className="flex items-center gap-2 flex-wrap">
            {(() => {
              const c = lead.identity_confidence || lead.verification_confidence || "unable_to_verify";
              const tone = IDENTITY_TONE[c] || IDENTITY_TONE.unable_to_verify;
              return <Badge variant="outline" className={tone.bg}>{tone.label}</Badge>;
            })()}
            {lead.is_franchise_chain && <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">Franchise/Chain</Badge>}
            {lead.needs_review && <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">Needs review</Badge>}
            {lead.review_status === "saved" && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Saved</Badge>}
            {lead.review_status === "rejected" && <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">Rejected</Badge>}
            {lead.lead_source === "google_places" && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Google verified</Badge>}
            {lead.lead_source === "hybrid_topup" && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">GPT top-up</Badge>}
            {lead.enrichment_skipped && (
              <Badge
                variant="outline"
                className="bg-zinc-100 text-zinc-700 border-zinc-300"
                data-testid="enrichment-skipped-badge"
                title={
                  lead.enrichment_skipped_reason === "website_unreachable"
                    ? "Website unreachable — AI enrichment skipped"
                    : "Identity unverifiable — AI enrichment skipped"
                }
              >
                Enrichment skipped
              </Badge>
            )}
            <Badge variant="outline">{lead.lead_source || "—"}</Badge>
            {(() => {
              const cs = lead.crawl_status || (lead.crawl_trace?.[0]?.crawl_status) || null;
              if (!cs) return null;
              const tones = {
                crawled:       "bg-emerald-50 text-emerald-700 border-emerald-200",
                bot_protected: "bg-amber-50 text-amber-700 border-amber-200",
                js_only:       "bg-amber-50 text-amber-700 border-amber-200",
                unreachable:   "bg-rose-50 text-rose-700 border-rose-200",
              };
              const labels = {
                crawled: "Crawled",
                bot_protected: "Bot-protected",
                js_only: "JS-only",
                unreachable: "Unreachable",
              };
              return (
                <Badge
                  variant="outline"
                  className={tones[cs] || "bg-zinc-100 text-zinc-700 border-zinc-200"}
                  data-testid="drawer-crawl-status"
                  title={`Crawl status: ${cs}`}
                >
                  {labels[cs] || cs}
                </Badge>
              );
            })()}
          </div>

          {/* Crawl/score timestamps */}
          {(lead.last_crawled_at || lead.last_scored_at) && (
            <div className="text-[10px] text-zinc-500 flex flex-wrap gap-x-3" data-testid="drawer-timestamps">
              {lead.last_crawled_at && (
                <span>Last crawled: {new Date(lead.last_crawled_at).toLocaleString()}</span>
              )}
              {lead.last_scored_at && lead.last_scored_at !== lead.last_crawled_at && (
                <span>Last scored: {new Date(lead.last_scored_at).toLocaleString()}</span>
              )}
            </div>
          )}

          {/* Save / Reject action bar */}
          <div className="flex items-center gap-2 flex-wrap pb-1" data-testid="lead-review-actions">
            {(!lead.review_status || lead.review_status === "pending") && (
              <>
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                  onClick={() => onSave?.(lead)}
                  data-testid="drawer-save-lead"
                >
                  <Check className="w-4 h-4 mr-1.5" /> Save lead
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 border-rose-200 text-rose-700 hover:bg-rose-50"
                  onClick={() => onReject?.(lead)}
                  data-testid="drawer-reject-lead"
                >
                  <X className="w-4 h-4 mr-1.5" /> Reject…
                </Button>
              </>
            )}
            {(lead.review_status === "saved" || lead.review_status === "rejected") && (
              <Button
                size="sm"
                variant="outline"
                className="h-8"
                onClick={() => onReset?.(lead)}
                data-testid="drawer-reset-review"
              >
                <RotateCcw className="w-4 h-4 mr-1.5" /> Move back to Pending
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              onClick={() => onRecrawl?.(lead)}
              disabled={!!recrawlingId}
              data-testid="drawer-recrawl-lead"
              title="Re-fetch website, re-extract evidence, re-score this lead"
            >
              <RefreshCw className={`w-4 h-4 mr-1.5 ${recrawlingId === lead.id ? "animate-spin" : ""}`} />
              {recrawlingId === lead.id ? "Re-crawling…" : "Re-crawl"}
            </Button>
            {lead.reviewed_by && (
              <span className="text-[11px] text-zinc-500 ml-auto">
                Reviewed by {lead.reviewed_by}
                {lead.reviewed_at && ` · ${new Date(lead.reviewed_at).toLocaleString()}`}
              </span>
            )}
          </div>

          {/* Rejection-reason banner (when applicable) */}
          {lead.review_status === "rejected" && lead.rejection_reason && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-900" data-testid="rejection-reason-banner">
              <div className="font-semibold mb-1">Rejection reason</div>
              <div>{lead.rejection_reason}</div>
            </div>
          )}

          {/* Needs-review explanation banner */}
          {lead.needs_review && Array.isArray(lead.needs_review_reasons) && lead.needs_review_reasons.length > 0 && (
            <div className="rounded-xl border border-orange-200 bg-orange-50 p-3 text-xs text-orange-900" data-testid="needs-review-banner">
              <div className="font-semibold mb-1">Why this lead needs review</div>
              <ul className="list-disc ml-5 space-y-0.5">
                {lead.needs_review_reasons.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}

          {/* Quality confidence chips */}
          <Card className="rounded-xl border-zinc-100">
            <CardContent className="p-3">
              <div className="text-xs uppercase tracking-wider text-zinc-400 mb-2">Data quality</div>
              <div className="flex flex-wrap gap-2 text-[11px]">
                <Badge variant="outline" className={RELEVANCE_TONE[lead.business_match_confidence || "unknown"]}>
                  Business match · {(lead.business_match_confidence || "unknown").toUpperCase()}
                </Badge>
                <Badge variant="outline" className={RELEVANCE_TONE[lead.category_relevance || "unknown"]}>
                  Category relevance · {(lead.category_relevance || "unknown").toUpperCase()}
                </Badge>
                <Badge variant="outline" className={RELEVANCE_TONE[lead.social_match_confidence || "unknown"]}>
                  Social match · {(lead.social_match_confidence || "unknown").toUpperCase()}
                </Badge>
                <Badge variant="outline" className={lead.location_specific_verified
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-zinc-100 text-zinc-500 border-zinc-200"}>
                  {lead.location_specific_verified ? "Location verified" : "Location unverified"}
                </Badge>
                {lead.is_franchise_chain && (
                  <Badge variant="outline" className={RELEVANCE_TONE[lead.franchise_confidence || "unknown"]}>
                    Franchise · {(lead.franchise_confidence || "unknown").toUpperCase()}
                  </Badge>
                )}
              </div>
              {lead.franchise_evidence && lead.is_franchise_chain && (
                <div className="text-[10px] text-zinc-500 mt-2">
                  <span className="text-zinc-400">Franchise evidence:</span> {lead.franchise_evidence}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Per-channel identity signals */}
          {Array.isArray(lead.identity_signals) && lead.identity_signals.length > 0 && (
            <Card className="rounded-xl border-zinc-100">
              <CardContent className="p-3">
                <div className="text-xs uppercase tracking-wider text-zinc-400 mb-2">Identity signals</div>
                <ul className="space-y-1 text-xs">
                  {lead.identity_signals.map((s, i) => {
                    const dot =
                      s.status === "match" ? "bg-emerald-500 text-white" :
                      s.status === "mismatch" ? "bg-rose-500 text-white" :
                      s.status === "unreachable" ? "bg-amber-500 text-white" :
                      s.status === "inconclusive" ? "bg-zinc-300 text-zinc-700" :
                      "bg-zinc-200 text-zinc-500";
                    const glyph =
                      s.status === "match" ? "✓" :
                      s.status === "mismatch" ? "✗" :
                      s.status === "unreachable" ? "!" : "·";
                    return (
                      <li key={i} className="flex items-start gap-2" data-testid={`identity-signal-${s.channel}`}>
                        <span className={`mt-0.5 inline-flex w-3.5 h-3.5 rounded-full text-[9px] items-center justify-center ${dot}`}>
                          {glyph}
                        </span>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={s.status === "match" ? "text-zinc-800" : "text-zinc-500"}>{s.label}</span>
                            <span className="text-[9px] uppercase tracking-wider text-zinc-400">{s.status}</span>
                          </div>
                          {s.detail && <div className="text-[10px] text-zinc-400">{s.detail}</div>}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Legacy verification signal breakdown */}
          {Array.isArray(lead.verification_signals) && lead.verification_signals.length > 0 && (
            <Card className="rounded-xl border-zinc-100">
              <CardContent className="p-3">
                <div className="text-xs uppercase tracking-wider text-zinc-400 mb-2">Verification signals</div>
                <ul className="space-y-1 text-xs">
                  {lead.verification_signals.map((s) => {
                    // Slice 3.2 — tri-state rendering. status defaults to
                    // "passed"/"failed" mirroring the boolean for back-compat.
                    const status = s.status || (s.passed ? "passed" : "failed");
                    const dotClass =
                      status === "passed"
                        ? "bg-emerald-500 text-white"
                        : status === "not_checked"
                        ? "bg-zinc-200 text-zinc-400 border border-zinc-300"
                        : "bg-zinc-200 text-zinc-500";
                    const symbol = status === "passed" ? "✓" : status === "not_checked" ? "?" : "·";
                    const labelClass =
                      status === "passed"
                        ? "text-zinc-800"
                        : status === "not_checked"
                        ? "text-zinc-500"
                        : "text-zinc-400";
                    return (
                      <li key={s.key} className="flex items-start gap-2">
                        <span className={`mt-0.5 inline-flex w-3.5 h-3.5 rounded-full text-[9px] items-center justify-center ${dotClass}`}>{symbol}</span>
                        <div>
                          <div className={labelClass}>
                            {s.label}
                            {status === "not_checked" && (
                              <span className="ml-1.5 text-[9px] uppercase tracking-wide text-zinc-400">not checked</span>
                            )}
                          </div>
                          {s.detail && <div className="text-[10px] text-zinc-400">{s.detail}</div>}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Phase */}
          <div className="flex items-center gap-2">
            <Label className="text-xs">Phase</Label>
            <Select value={lead.lead_phase} onValueChange={changePhase} disabled={savingPhase}>
              <SelectTrigger className="w-40" data-testid="phase-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PHASE_OPTIONS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
            {savingPhase && <Loader2 className="w-3 h-3 animate-spin text-zinc-400" />}
          </div>

          {/* Scores — deterministic v2 */}
          <ScoreBreakdownPanel lead={lead} />
          <RecommendationsPanel leadId={lead.id} />
          <OutreachPanel
            leadId={lead.id}
            leadName={lead.business_name}
            lead={lead}
            onLeadUpdate={(patch) => onChanged?.({ ...lead, ...patch })}
          />

          <Tabs defaultValue="overview" className="w-full">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="booking">Booking</TabsTrigger>
              <TabsTrigger value="presence">Presence</TabsTrigger>
              <TabsTrigger value="trace" data-testid="trace-tab">Crawl Trace</TabsTrigger>
              <TabsTrigger value="notes">Notes</TabsTrigger>
            </TabsList>
            <TabsContent value="overview" className="space-y-3">
              {lead.enrichment_skipped && (
                <div
                  className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2 text-xs"
                  data-testid="enrichment-skipped-notice"
                >
                  <div className="font-medium">AI enrichment skipped</div>
                  <div className="mt-0.5 text-amber-800">
                    {lead.enrichment_skipped_reason === "website_unreachable"
                      ? "We could not load this business's website, so we did not generate an AI summary, services list, customer journey or opportunity score. Verification signals below are real."
                      : "We could not confirm this business's identity from independent sources, so we did not generate AI-derived content. Verification signals below are real."}
                  </div>
                </div>
              )}
              <KV label="Most important reason" value={lead.most_important_reason} />
              <KV label="Summary" value={lead.business_summary} />
              <KV label="Services" value={(lead.services || []).join(", ") || null} />
              <KV label="Customer journey" value={(lead.customer_journey || []).join(" → ") || null} />
              <KV label="Verification" value={lead.identity_evidence || lead.verification_evidence} />
              <KV label="Address" value={lead.address} source={lead.source_evidence?.address} />
              <KV label="Location" value={[lead.city, lead.state, lead.country].filter(Boolean).join(", ")} />
              <KV label="Category" value={lead.category} source={lead.source_evidence?.category} />
              <ContactRow lead={lead} />
            </TabsContent>
            <TabsContent value="booking" className="space-y-2">
              <KV label="Booking available" value={lead.booking_available === true ? "Yes" : (lead.booking_available === false ? "No" : null)} />
              <KV label="Platform" value={lead.booking_platform_name} />
              <KV label="Type" value={lead.booking_platform_type} />
              <KV label="Platform confidence" value={lead.booking_platform_confidence?.toUpperCase()} />
              <KV label="Signal confidence" value={lead.booking_signal_confidence?.toUpperCase()} />
              <KV label="Primary method" value={lead.primary_booking_method} />
              <KV label="Secondary method" value={lead.secondary_booking_method} />
              <KV label="Pricing visibility" value={lead.pricing_visibility} />
              <KV label="Review information" value={lead.review_information} />
              {Array.isArray(lead.booking_signals) && lead.booking_signals.length > 0 && (
                <Card className="rounded-xl border-zinc-100">
                  <CardContent className="p-3">
                    <div className="text-xs uppercase tracking-wider text-zinc-400 mb-2">Booking signals from website</div>
                    <ul className="space-y-1 text-xs">
                      {lead.booking_signals.map((s, i) => (
                        <li key={i} className="flex items-start gap-2" data-testid={`booking-signal-${s.type}-${i}`}>
                          <span className={`mt-0.5 inline-flex w-3.5 h-3.5 rounded-full text-[9px] items-center justify-center ${
                            s.strength === "high" ? "bg-emerald-500 text-white" :
                            s.strength === "medium" ? "bg-amber-500 text-white" : "bg-zinc-200 text-zinc-600"
                          }`}>✓</span>
                          <div className="flex-1">
                            <div className="text-zinc-800">
                              {s.platform && <span className="font-medium">{s.platform}</span>}
                              {s.text && <span className="italic">"{s.text}"</span>}
                              {s.href && <span className="text-zinc-500 break-all">{s.href}</span>}
                              <span className="ml-2 text-[9px] uppercase tracking-wider text-zinc-400">{s.type.replace(/_/g, " ")}</span>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
            <TabsContent value="presence" className="space-y-2">
              <KV label="Online presence" value={lead.online_presence_summary} />
              <KV label="Contact methods" value={(lead.customer_contact_methods || []).join(", ") || null} />
              <KV label="Google rating" value={lead.google_rating != null ? `${lead.google_rating} (${lead.google_review_count ?? 0} reviews)` : null} />
              <KV label="Google Business" value={lead.google_business_url} link />
              <KV label="Instagram" value={lead.instagram_url} link source={lead.source_evidence?.instagram_url} />
              <KV label="Facebook" value={lead.facebook_url} link source={lead.source_evidence?.facebook_url} />
              <KV label="LinkedIn" value={lead.linkedin_company_url} link source={lead.source_evidence?.linkedin_company_url} />
            </TabsContent>
            <TabsContent value="trace" className="space-y-3">
              <CrawlTrace lead={lead} />
            </TabsContent>
            <TabsContent value="notes">
              <Textarea rows={6} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Private notes for this lead…" data-testid="notes-textarea" />
              <div className="flex justify-end mt-2">
                <Button size="sm" onClick={saveNotes} disabled={savingNotes} data-testid="save-notes-btn">
                  {savingNotes ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : null} Save notes
                </Button>
              </div>
            </TabsContent>
          </Tabs>

          <div className="pt-4 border-t flex justify-end">
            <Button variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={handleDelete} data-testid="delete-lead-btn">
              Delete lead
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function CrawlTrace({ lead }) {
  const trace = Array.isArray(lead.crawl_trace) ? lead.crawl_trace : [];
  const emails = lead.discovered_emails || [];
  const socials = lead.discovered_socials || [];
  const bookingAnchors = lead.discovered_booking_anchors || [];
  const caps = lead.capability_states || {};

  if (trace.length === 0 && emails.length === 0 && socials.length === 0 && bookingAnchors.length === 0) {
    return (
      <div className="text-xs text-zinc-500 italic" data-testid="trace-empty">
        No crawl trace recorded for this lead. The crawler may not have been run, or this lead pre-dates the trace feature.
      </div>
    );
  }

  return (
    <div className="space-y-4 text-xs" data-testid="crawl-trace">
      {/* Pages crawled */}
      <Section title="Pages Crawled">
        {trace.length === 0
          ? <Empty>No pages were fetched.</Empty>
          : (
            <ul className="space-y-1">
              {trace.map((p, i) => (
                <li key={i} className="flex items-start gap-2" data-testid={`trace-page-${i}`}>
                  <span className={`mt-0.5 ${p.reachable ? "text-emerald-600" : "text-rose-500"}`}>{p.reachable ? "✓" : "✗"}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono break-all text-zinc-800">{p.final_url || p.url}</div>
                    <div className="text-zinc-500">
                      HTTP {p.status_code ?? "—"} · {Math.round((p.body_size || 0) / 1024)} kB
                      {p.error ? <span className="text-rose-500"> · {p.error}</span> : null}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
      </Section>

      {/* Social links */}
      <Section title={`Social Links Found (${socials.length})`}>
        {socials.length === 0
          ? <Empty>No social anchors detected on the fetched pages.</Empty>
          : (
            <ul className="space-y-2">
              {socials.map((s, i) => (
                <li key={i} className="rounded-md border border-zinc-100 bg-zinc-50/50 p-2" data-testid={`trace-social-${s.network}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-emerald-600">✓</span>
                    <span className="font-medium capitalize">{s.network} found</span>
                  </div>
                  <TraceEvidence
                    source={`Homepage ${s.zone || "body"}`}
                    url={s.url}
                    method={s.source || "anchor_href"}
                    page={s.page_url}
                  />
                </li>
              ))}
            </ul>
          )}
      </Section>

      {/* Emails */}
      <Section title={`Emails Found (${emails.length})`}>
        {emails.length === 0
          ? <Empty>No emails detected on the fetched pages.</Empty>
          : (
            <ul className="space-y-2">
              {emails.map((e, i) => (
                <li key={i} className="rounded-md border border-zinc-100 bg-zinc-50/50 p-2" data-testid={`trace-email-${i}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-emerald-600">✓</span>
                    <span className="font-mono font-medium text-zinc-800 break-all">{e.value}</span>
                  </div>
                  <TraceEvidence
                    source={`${e.zone === "footer" ? "Homepage footer" : e.zone === "header" ? "Homepage header" : e.zone === "icons" ? "Social icon strip" : "Homepage body"}`}
                    method={e.source || "mailto"}
                    page={e.page_url}
                  />
                </li>
              ))}
            </ul>
          )}
      </Section>

      {/* Booking anchors + platform */}
      <Section title={`Booking Links Found (${bookingAnchors.length})`}>
        {bookingAnchors.length === 0
          ? <Empty>No booking-platform anchors detected.</Empty>
          : (
            <ul className="space-y-2">
              {bookingAnchors.map((b, i) => (
                <li key={i} className="rounded-md border border-zinc-100 bg-zinc-50/50 p-2" data-testid={`trace-booking-${i}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-emerald-600">✓</span>
                    <span className="font-medium">{b.platform || "Booking candidate"}</span>
                    {b.detection_method && (
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${
                        b.detection_method === "known_host"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : b.detection_method === "ai"
                          ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                          : "bg-amber-50 text-amber-700 border-amber-200"
                      }`}>
                        {b.detection_method === "known_host" ? "verified" :
                         b.detection_method === "ai" ? "AI detected" :
                         b.detection_method.replace("_", " ")}
                      </span>
                    )}
                  </div>
                  <TraceEvidence
                    source={`Homepage ${b.zone || "body"}`}
                    url={b.url}
                    method={b.detection_method === "known_host" ? "KNOWN_BOOKING_HOSTS match" : b.detection_method}
                    page={b.page_url}
                  />
                  {Array.isArray(b.evidence) && b.evidence.length > 0 && (
                    <ul className="mt-1 ml-4 text-[10px] text-zinc-500 list-disc">
                      {b.evidence.map((e, j) => <li key={j}>{e}</li>)}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
      </Section>

      {lead.booking_platform_name && (
        <Section title="Booking Platform">
          <div className="rounded-md border border-emerald-100 bg-emerald-50/50 p-2" data-testid="trace-booking-platform">
            <div className="flex items-center gap-2">
              <span className="text-emerald-600">✓</span>
              <span className="font-semibold text-zinc-900">{lead.booking_platform_name}</span>
              {lead.booking_platform_confidence && (
                <Badge variant="outline" className="text-[10px] capitalize">
                  {lead.booking_platform_confidence} confidence
                </Badge>
              )}
            </div>
          </div>
        </Section>
      )}

      {/* Capability states */}
      <Section title="Capability States">
        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
          {Object.entries(caps).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between" data-testid={`trace-cap-${k}`}>
              <span className="text-zinc-600 capitalize">{k.replace(/_/g, " ")}</span>
              <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${
                v === "verified" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                v === "mismatch" ? "bg-rose-50 text-rose-700 border-rose-200" :
                v === "multi_location" ? "bg-sky-50 text-sky-700 border-sky-200" :
                v === "not_found" ? "bg-zinc-50 text-zinc-500 border-zinc-200" :
                "bg-amber-50 text-amber-700 border-amber-200"
              }`}>{v === "multi_location" ? "MULTI-LOCATION" : v.replace("_", " ")}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* Slice 1.1: Schema markup evidence */}
      <SchemaEvidenceSection lead={lead} />

      {/* Slice 1.1: Contact form evidence */}
      <ContactFormSection lead={lead} />
    </div>
  );
}

function SchemaEvidenceSection({ lead }) {
  const homepageTrace = (lead.crawl_trace || []).find(t => t?.page_kind === "homepage");
  const ev = homepageTrace?.schema_evidence
    || (lead.schema_types !== undefined ? {
         schema_detected: (lead.schema_types || []).length > 0,
         schema_types: lead.schema_types || [],
         blocks_found: lead.schema_blocks_found || 0,
         blocks_parsed: lead.schema_blocks_parsed || 0,
         has_no_type_block: !!lead.schema_has_no_type_block,
         has_business_relevant: !!lead.schema_business_relevant,
       } : null);
  if (!ev) return null;
  const types = ev.schema_types || [];
  const blocksFound = ev.blocks_found || 0;
  // Three-state classification matching the scorer.
  let state, tone;
  if (types.length > 0 && ev.has_business_relevant) {
    state = "Business schema detected";
    tone = "text-emerald-700";
  } else if (types.length > 0) {
    state = "Schema detected but limited (generic only)";
    tone = "text-amber-700";
  } else if (blocksFound > 0) {
    state = ev.has_no_type_block
      ? `Schema detected but limited (${blocksFound} block${blocksFound === 1 ? "" : "s"}, no @type)`
      : `Schema detected but limited (${blocksFound} block${blocksFound === 1 ? "" : "s"}, parse failed)`;
    tone = "text-amber-700";
  } else {
    state = "No JSON-LD schema markup detected";
    tone = "text-zinc-500";
  }
  return (
    <Section title={`Schema Markup${types.length > 0 ? ` (${types.length})` : ""}`}>
      <div className="space-y-2" data-testid="trace-schema-evidence">
        <div className="flex items-center gap-2">
          <span className={ev.has_business_relevant ? "text-emerald-600" : (types.length > 0 || blocksFound > 0 ? "text-amber-600" : "text-zinc-400")}>
            {ev.has_business_relevant ? "✓" : (types.length > 0 || blocksFound > 0 ? "·" : "—")}
          </span>
          <span className={`font-medium ${tone}`}>{state}</span>
        </div>
        {(blocksFound > 0) && (
          <div className="text-[10px] text-zinc-500" data-testid="trace-schema-blocks">
            {blocksFound} JSON-LD script block{blocksFound === 1 ? "" : "s"} on page
            {(ev.blocks_parsed !== undefined && ev.blocks_parsed !== blocksFound) &&
              <span> · {ev.blocks_parsed} parsed cleanly</span>}
          </div>
        )}
        {types.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {types.map((t, i) => (
              <span
                key={`st-${i}`}
                className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${
                  ["localbusiness", "hairsalon", "beautysalon", "spa", "restaurant", "store",
                   "medicalbusiness", "medicalclinic", "dentist", "professionalservice",
                   "organization", "service", "product", "offer", "review", "aggregaterating"]
                    .includes(t.toLowerCase())
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-zinc-50 text-zinc-500 border-zinc-200"
                }`}
                data-testid={`trace-schema-type-${t.toLowerCase()}`}
              >
                {t}
              </span>
            ))}
          </div>
        )}
      </div>
    </Section>
  );
}

function ContactFormSection({ lead }) {
  const fromTop = lead.contact_form_evidence;
  const fromTrace = (lead.crawl_trace || []).find(
    p => p && p.contact_form?.has_form
  )?.contact_form;
  const cf = (fromTop && fromTop.has_form) ? fromTop : fromTrace;
  return (
    <Section title="Contact Form">
      {!cf || !cf.has_form
        ? <Empty>No contact form detected on the homepage or /contact subpage.</Empty>
        : (
          <div className="space-y-1" data-testid="trace-contact-form">
            <div className="flex items-center gap-2">
              <span className="text-emerald-600">✓</span>
              <span className="font-medium text-zinc-800">Contact form available</span>
            </div>
            {cf.page_url && (
              <a
                href={cf.page_url}
                target="_blank" rel="noreferrer"
                className="text-[11px] text-blue-700 hover:underline font-mono break-all"
              >{cf.page_url}</a>
            )}
            {cf.evidence && (
              <div className="text-[10px] text-zinc-500">Detected via: {cf.evidence}</div>
            )}
          </div>
        )}
    </Section>
  );
}

function Section({ title, children }) {
  return (
    <Card className="rounded-xl border-zinc-100">
      <CardContent className="p-3">
        <div className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2 font-semibold">{title}</div>
        {children}
      </CardContent>
    </Card>
  );
}

function Empty({ children }) {
  return <div className="text-zinc-400 italic">{children}</div>;
}

function TraceEvidence({ source, url, method, page }) {
  return (
    <div className="ml-6 grid grid-cols-[64px_1fr] gap-x-2 gap-y-0.5 text-[11px]">
      {source && (
        <>
          <span className="text-zinc-400">Source:</span>
          <span className="text-zinc-700">{source}</span>
        </>
      )}
      {url && (
        <>
          <span className="text-zinc-400">URL:</span>
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="text-indigo-600 hover:underline break-all"
          >
            {url}
          </a>
        </>
      )}
      {method && (
        <>
          <span className="text-zinc-400">Method:</span>
          <span className="text-zinc-600 font-mono">{method}</span>
        </>
      )}
      {page && page !== url && (
        <>
          <span className="text-zinc-400">Page:</span>
          <span className="text-zinc-500 break-all">{page}</span>
        </>
      )}
    </div>
  );
}

function KV({ label, value, link, source }) {
  const sourceBadge = source ? (
    <span
      className={`ml-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${SOURCE_TONE[source] || SOURCE_TONE.gpt}`}
      data-testid={`source-evidence-${source}`}
      title={`Source: ${source}`}
    >
      {source}
    </span>
  ) : null;
  if (!value || value === "Not Visible") {
    return (
      <div className="text-sm">
        <span className="text-zinc-400 mr-2">{label}:</span>
        <span className="text-zinc-400 italic">Not Visible</span>
        {sourceBadge}
      </div>
    );
  }
  return (
    <div className="text-sm">
      <span className="text-zinc-400 mr-2">{label}:</span>
      {link
        ? <a href={value} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline inline-flex items-center gap-1">{String(value).slice(0, 60)}…<ExternalLink className="w-3 h-3" /></a>
        : <span className="text-zinc-800">{value}</span>}
      {sourceBadge}
    </div>
  );
}

function ContactRow({ lead }) {
  const links = [];
  if (lead.website) links.push({ icon: Globe, href: lead.website, label: "Website" });
  if (lead.phone) links.push({ icon: Phone, href: `tel:${lead.phone}`, label: lead.phone });
  if (lead.google_business_url) links.push({ icon: Star, href: lead.google_business_url, label: "Google" });
  if (lead.instagram_url) links.push({ icon: Instagram, href: lead.instagram_url, label: lead.instagram_handle || "Instagram" });
  if (lead.facebook_url) links.push({ icon: Facebook, href: lead.facebook_url, label: "Facebook" });
  if (lead.linkedin_company_url) links.push({ icon: Linkedin, href: lead.linkedin_company_url, label: "LinkedIn" });

  // All discovered emails (deduped against the primary email already on lead)
  const discoveredEmails = (lead.discovered_emails || [])
    .map(e => (typeof e === "string" ? e : e?.value))
    .filter(Boolean)
    .map(s => s.toLowerCase());
  const primaryEmail = (lead.email || "").toLowerCase();
  const extraEmails = Array.from(new Set(discoveredEmails)).filter(e => e !== primaryEmail);

  // Contact form availability — read from outcome or homepage trace
  const cfFromTop = lead.contact_form_evidence;
  const cfFromTrace = (lead.crawl_trace || []).find(
    p => p && p.page_kind === "homepage" && p.contact_form?.has_form
  )?.contact_form;
  const contactForm = (cfFromTop && cfFromTop.has_form) ? cfFromTop : cfFromTrace;

  if (!links.length && !primaryEmail && extraEmails.length === 0 && !contactForm) return null;
  return (
    <div className="space-y-1.5 pt-1" data-testid="contact-row">
      <div className="flex gap-2 flex-wrap">
        {primaryEmail && (
          <a
            href={`mailto:${primaryEmail}`}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-emerald-200 bg-emerald-50/40 text-emerald-800 hover:bg-emerald-50"
            data-testid="contact-email-primary"
          >
            <Star className="w-3 h-3" /> {primaryEmail}
          </a>
        )}
        {extraEmails.map((em, i) => (
          <a
            key={`em-${i}`}
            href={`mailto:${em}`}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-zinc-200 hover:bg-zinc-50"
            data-testid={`contact-email-extra-${i}`}
            title="Discovered on the website"
          >
            {em}
          </a>
        ))}
        {links.map((L, i) => (
          <a key={i} href={L.href} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-zinc-200 hover:bg-zinc-50">
            <L.icon className="w-3 h-3" /> {L.label}
          </a>
        ))}
        {contactForm && (
          <a
            href={contactForm.page_url || lead.website || "#"}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-blue-200 bg-blue-50/40 text-blue-800 hover:bg-blue-50"
            data-testid="contact-form-badge"
            title={contactForm.evidence ? `Detected via: ${contactForm.evidence}` : "Contact form detected"}
          >
            <Check className="w-3 h-3" /> Contact form
          </a>
        )}
      </div>
      {extraEmails.length > 0 && (
        <div className="text-[10px] text-zinc-400" data-testid="contact-email-extra-count">
          {extraEmails.length} additional email{extraEmails.length === 1 ? "" : "s"} discovered on the website
        </div>
      )}
    </div>
  );
}

function ManualCreateDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ business_name: "", website: "", category: "", city: "", state: "", country: "Canada", phone: "", notes: "" });
  const [saving, setSaving] = useState(false);
  if (!open) return null;
  const submit = async () => {
    if (!form.business_name.trim()) { toast.error("Business name required"); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/lead-intel/leads`, form, { headers: tokenHeader() });
      toast.success("Lead created");
      onCreated?.(r.data); onClose?.();
      setForm({ business_name: "", website: "", category: "", city: "", state: "", country: "Canada", phone: "", notes: "" });
    } catch (e) {
      if (e?.response?.status === 409) toast.error("Already exists");
      else toast.error("Create failed");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-md" onClick={e => e.stopPropagation()}>
        <CardHeader><CardTitle>Add lead manually</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {[
            ["business_name", "Business name *"], ["website", "Website"], ["category", "Category"],
            ["city", "City"], ["state", "State/Province"], ["country", "Country"], ["phone", "Phone"],
          ].map(([k, lbl]) => (
            <div key={k}>
              <Label className="text-xs">{lbl}</Label>
              <Input value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`manual-${k}`} />
            </div>
          ))}
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="manual-create-submit">
              {saving ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : null} Create
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


function RejectLeadDialog({ state, reasons, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [detail, setDetail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (state?.open) {
      setReason("");
      setDetail("");
      setSubmitting(false);
    }
  }, [state?.open, state?.lead?.id]);

  if (!state?.open) return null;
  const lead = state.lead;

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm?.(reason || null, detail.trim() || null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="reject-modal-overlay"
    >
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <CardHeader>
          <CardTitle className="text-base">Reject lead</CardTitle>
          {lead && <p className="text-xs text-zinc-500">{lead.business_name}</p>}
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs">Reason (optional)</Label>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {reasons.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setReason(reason === r ? "" : r)}
                  data-testid={`reject-reason-${r.replace(/\W+/g, "-").toLowerCase()}`}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    reason === r
                      ? "bg-rose-50 border-rose-300 text-rose-700"
                      : "bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
          {reason === "Other" && (
            <div>
              <Label className="text-xs">Details</Label>
              <Textarea
                rows={3}
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
                placeholder="Briefly describe why this lead is being rejected…"
                data-testid="reject-detail-textarea"
              />
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose} data-testid="reject-cancel">Cancel</Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={handleConfirm}
              disabled={submitting}
              data-testid="reject-confirm"
            >
              {submitting ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : <X className="w-3 h-3 mr-2" />}
              Reject lead
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function BulkSendModal({ open, ctx, onClose, onSubmit }) {
  const [templateId, setTemplateId] = useState("");
  const [senderId, setSenderId] = useState("");
  const [includeAlready, setIncludeAlready] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Pre-select the Okliko Phase 0 template if present, else the first template.
  useEffect(() => {
    if (!open) return;
    const tmpls = ctx?.templates || [];
    if (!tmpls.length) { setTemplateId(""); return; }
    const phase0 = tmpls.find(t => (t.name || "").toLowerCase().includes("phase 0"))
                || tmpls.find(t => (t.name || "").toLowerCase().includes("okliko"))
                || tmpls[0];
    setTemplateId(phase0.id);
    setSenderId("");
    setIncludeAlready(false);
    setSubmitting(false);
  }, [open, ctx?.templates]);

  if (!open) return null;
  const loading = ctx?.loading;
  const templates = ctx?.templates || [];
  const senders = ctx?.senders || [];
  const cap = ctx?.capInfo || { daily_cap: 0, sent_today: 0, remaining: 0 };
  const total = ctx?.leadIds?.length || 0;
  const willFit = Math.min(total, cap.remaining || 0);
  const overCap = Math.max(0, total - (cap.remaining || 0));

  const canSubmit = !loading && !submitting && templates.length > 0 && !!templateId && total > 0;

  const handleSend = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onSubmit({
        template_id: templateId,
        from_sender_id: senderId || null,
        include_already_emailed: includeAlready,
      });
    } catch { /* toast handled by parent */ }
    finally { setSubmitting(false); }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={submitting ? undefined : onClose}
      data-testid="bulk-send-modal-overlay"
    >
      <Card className="w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="bulk-send-modal">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="w-4 h-4 text-indigo-600" />
            Send outreach to {total} lead{total === 1 ? "" : "s"}
          </CardTitle>
          <p className="text-xs text-zinc-500">
            Each email is personalised per lead. Suppressed and previously-emailed
            leads are skipped automatically.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="py-8 flex items-center justify-center text-zinc-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading options…
            </div>
          ) : (
            <>
              {/* Daily cap indicator */}
              <div
                className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2.5 text-xs"
                data-testid="bulk-send-cap-indicator"
              >
                <div className="flex items-center justify-between">
                  <span className="text-zinc-600">
                    Daily send budget · <b>{cap.sent_today}</b>/{cap.daily_cap} used
                  </span>
                  <span className={`font-medium ${cap.remaining > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                    {cap.remaining} left today
                  </span>
                </div>
                <div className="mt-1.5 text-zinc-500">
                  {overCap > 0 ? (
                    <>Will send <b>{willFit}</b> of {total} now · <b className="text-amber-700">{overCap}</b> will be skipped (over cap)</>
                  ) : (
                    <>All <b>{total}</b> selected fit within today&apos;s remaining budget.</>
                  )}
                </div>
              </div>

              {/* Template picker */}
              <div>
                <Label className="text-xs">Email template</Label>
                {templates.length === 0 ? (
                  <div className="mt-1 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5">
                    No outreach templates configured. Create one in Settings → Templates first.
                  </div>
                ) : (
                  <Select value={templateId} onValueChange={setTemplateId}>
                    <SelectTrigger data-testid="bulk-send-template" className="mt-1"><SelectValue placeholder="Pick a template" /></SelectTrigger>
                    <SelectContent>
                      {templates.map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name || t.subject || "Untitled"}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Sender picker (optional — falls back to workspace default) */}
              <div>
                <Label className="text-xs">From sender {senders.length > 0 ? "(optional)" : ""}</Label>
                {senders.length === 0 ? (
                  <div className="mt-1 text-xs text-zinc-500">
                    Using workspace default sender. Add rotating senders in Settings → Outreach.
                  </div>
                ) : (
                  <Select value={senderId || "__default__"} onValueChange={(v) => setSenderId(v === "__default__" ? "" : v)}>
                    <SelectTrigger data-testid="bulk-send-sender" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__default__">Workspace default</SelectItem>
                      {senders.map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.from_name ? `${s.from_name} <${s.from_email}>` : s.from_email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Skip already-emailed toggle */}
              <label
                className="flex items-start gap-2 cursor-pointer select-none rounded-lg border border-zinc-200 px-3 py-2.5"
                data-testid="bulk-send-include-already-wrap"
              >
                <input
                  type="checkbox"
                  className="mt-0.5 w-4 h-4 accent-indigo-600"
                  checked={includeAlready}
                  onChange={(e) => setIncludeAlready(e.target.checked)}
                  data-testid="bulk-send-include-already"
                />
                <div className="text-xs">
                  <div className="font-medium text-zinc-800">Re-send to leads you&apos;ve already emailed</div>
                  <div className="text-zinc-500">
                    Off (recommended) — previously-emailed leads are skipped to prevent re-spam.
                  </div>
                </div>
              </label>
            </>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={onClose} disabled={submitting} data-testid="bulk-send-cancel">
              Cancel
            </Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={handleSend}
              disabled={!canSubmit}
              data-testid="bulk-send-confirm"
            >
              {submitting
                ? <><Loader2 className="w-3 h-3 animate-spin mr-2" /> Sending…</>
                : <><Send className="w-3 h-3 mr-2" /> Send now</>}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}




function DiscoveryRunsPanel({ runs, expanded, setExpanded, activeRunId, setActiveRunId, onRename, onDelete, onArchive, onRecrawl, recrawlingRunId, onOpenHistory }) {
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null); // { run, cascade }

  if (!runs || runs.length === 0) {
    return null;
  }

  // Dashboard shows ONLY Current Run + last 3 non-archived runs.
  // Older runs live in the Run History sheet.
  const currentRun = activeRunId ? runs.find(r => r.id === activeRunId) : null;
  const recentRuns = runs
    .filter(r => !r.archived && r.id !== activeRunId)
    .slice(0, 3);
  const totalRuns = runs.length;
  const headerLabel = currentRun
    ? `Filtering by run: ${currentRun.run_name || `${currentRun.category} · ${currentRun.city}`}`
    : `Discovery runs`;

  return (
    <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }} data-testid="discovery-runs-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="text-sm font-medium text-zinc-800 flex items-center gap-2 hover:text-indigo-700"
            data-testid="runs-panel-toggle"
          >
            <Sparkles className="w-4 h-4 text-indigo-500" />
            {headerLabel}
            <span className="text-[10px] text-zinc-400">{expanded ? "▾" : "▸"}</span>
          </button>
          <div className="flex items-center gap-3">
            {activeRunId && (
              <button
                type="button"
                onClick={() => setActiveRunId(null)}
                className="text-xs text-indigo-600 hover:underline"
                data-testid="runs-panel-clear-filter"
              >
                Show all runs
              </button>
            )}
            <button
              type="button"
              onClick={onOpenHistory}
              className="text-xs text-indigo-600 hover:underline font-medium"
              data-testid="runs-panel-view-all"
            >
              View all runs ({totalRuns}) →
            </button>
          </div>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="pt-0 space-y-3">
          {/* Current Run — only shown when a run is selected */}
          {currentRun && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold mb-1.5">Current run</div>
              <RunCard
                run={currentRun}
                isActive={true}
                isEditing={editingId === currentRun.id}
                editName={editName}
                setEditName={setEditName}
                setEditingId={setEditingId}
                onActivate={() => setActiveRunId(null)}
                onRename={onRename}
                onArchive={onArchive}
                onRecrawl={onRecrawl}
                recrawlingRunId={recrawlingRunId}
                onConfirmDelete={setConfirmDelete}
              />
            </div>
          )}

          {/* Recent runs (last 3) */}
          {recentRuns.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold mb-1.5">
                Recent runs {currentRun ? "" : `(last ${recentRuns.length})`}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="recent-runs-grid">
                {recentRuns.map(r => (
                  <RunCard
                    key={r.id}
                    run={r}
                    isActive={false}
                    isEditing={editingId === r.id}
                    editName={editName}
                    setEditName={setEditName}
                    setEditingId={setEditingId}
                    onActivate={() => setActiveRunId(r.id)}
                    onRename={onRename}
                    onArchive={onArchive}
                    onRecrawl={onRecrawl}
                    recrawlingRunId={recrawlingRunId}
                    onConfirmDelete={setConfirmDelete}
                  />
                ))}
              </div>
            </div>
          )}

          {totalRuns > recentRuns.length + (currentRun ? 1 : 0) && (
            <div className="text-center pt-1">
              <button
                type="button"
                onClick={onOpenHistory}
                className="text-xs text-indigo-600 hover:underline"
                data-testid="runs-panel-view-all-bottom"
              >
                +{totalRuns - recentRuns.length - (currentRun ? 1 : 0)} more in Run History →
              </button>
            </div>
          )}
        </CardContent>
      )}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setConfirmDelete(null)}
          data-testid="run-delete-modal-overlay"
        >
          <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-base">Delete discovery run?</CardTitle>
              <p className="text-xs text-zinc-500">
                {confirmDelete.run.run_name || `${confirmDelete.run.category} · ${confirmDelete.run.city}`}
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-zinc-700">
                By default we'll only delete the run record. The {confirmDelete.run.total_leads ?? 0} lead{confirmDelete.run.total_leads === 1 ? "" : "s"} discovered during this run will stay in your database.
              </p>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={confirmDelete.cascade}
                  onChange={(e) => setConfirmDelete({ ...confirmDelete, cascade: e.target.checked })}
                  data-testid="run-delete-cascade"
                  className="mt-1"
                />
                <span>
                  Also delete all <b>{confirmDelete.run.total_leads ?? 0}</b> lead{confirmDelete.run.total_leads === 1 ? "" : "s"} from this run (irreversible).
                </span>
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setConfirmDelete(null)}>Cancel</Button>
                <Button
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  onClick={() => { onDelete(confirmDelete.run.id, confirmDelete.cascade); setConfirmDelete(null); }}
                  data-testid="run-delete-confirm"
                >
                  Delete{confirmDelete.cascade ? " run + leads" : " run"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Card>
  );
}

function RunCard({ run, isActive, isEditing, editName, setEditName, setEditingId, onActivate, onRename, onArchive, onRecrawl, recrawlingRunId, onConfirmDelete }) {
  const r = run;
  return (
    <div
      data-testid={`run-card-${r.id}`}
      className={`relative rounded-xl border p-3 transition-colors cursor-pointer ${
        isActive
          ? "border-indigo-300 bg-indigo-50/40"
          : "border-zinc-200 bg-white hover:border-zinc-300"
      }`}
      onClick={() => !isEditing && onActivate()}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="min-w-0 flex-1">
          {isEditing ? (
            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              <Input
                autoFocus
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { onRename(r.id, editName.trim() || null); setEditingId(null); }
                  if (e.key === "Escape") setEditingId(null);
                }}
                placeholder="Run name…"
                className="h-7 text-xs"
                data-testid={`run-name-input-${r.id}`}
              />
              <Button size="sm" className="h-7 px-2" onClick={() => { onRename(r.id, editName.trim() || null); setEditingId(null); }} data-testid={`run-rename-save-${r.id}`}>
                <Check className="w-3 h-3" />
              </Button>
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditingId(null)}>
                <X className="w-3 h-3" />
              </Button>
            </div>
          ) : (
            <h4 className="text-sm font-medium text-zinc-900 truncate" title={r.run_name || ""}>
              {r.run_name || <span className="text-zinc-400 italic">Unnamed run</span>}
            </h4>
          )}
          <div className="text-[11px] text-zinc-500 truncate">
            {r.category} · {r.city}{r.state ? `, ${r.state}` : ""}
          </div>
          <div className="text-[10px] text-zinc-400" data-testid={`run-summary-${r.id}`}>
            {new Date(r.created_at).toLocaleString()}
            {" · "}<span className="text-zinc-600 font-medium">{r.requested_results} req</span>
            {" · "}<span className="text-emerald-700 font-medium">{r.inserted ?? 0} inserted</span>
            {(r.pending_count ?? 0) > 0 && (
              <>{" · "}<span className="text-zinc-600">{r.pending_count} pending</span></>
            )}
            {(r.duplicates_skipped ?? 0) > 0 && (
              <span
                className="ml-1 text-zinc-500"
                title={`${r.duplicates_active ?? 0} active · ${r.duplicates_rejected ?? 0} rejected`}
              >
                · {r.duplicates_skipped} dup
                {((r.duplicates_active ?? 0) + (r.duplicates_rejected ?? 0)) > 0 && (
                  <span className="text-zinc-400"> ({r.duplicates_active ?? 0}a · {r.duplicates_rejected ?? 0}r)</span>
                )}
              </span>
            )}
            {(r.inserted ?? 0) < (r.requested_results ?? 0) && (
              <span
                className="ml-1 text-amber-600"
                title={r.exhaustion_reason ? `Stopped: ${r.exhaustion_reason}` : "Search pool exhausted or safety cap reached"}
              >
                · short {(r.requested_results ?? 0) - (r.inserted ?? 0)}
              </span>
            )}
          </div>
          {r.discovery_mode && (
            <div className="text-[10px] mt-1">
              <span className={`px-1.5 py-0.5 rounded border ${
                r.discovery_mode === "places_only" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                r.discovery_mode === "places_plus_gpt" ? "bg-indigo-50 text-indigo-700 border-indigo-200" :
                "bg-zinc-100 text-zinc-600 border-zinc-200"
              }`}>
                {r.discovery_mode === "places_only" ? "Places only" :
                 r.discovery_mode === "places_plus_gpt" ? "Places + GPT" :
                 "GPT only"}
              </span>
              {r.avg_opportunity_score != null && (
                <span className="ml-1 px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">
                  avg opp {r.avg_opportunity_score}
                </span>
              )}
            </div>
          )}
        </div>
        {!isEditing && (
          <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => { setEditingId(r.id); setEditName(r.run_name || ""); }}
              className="text-[10px] text-indigo-600 hover:underline"
              data-testid={`run-rename-${r.id}`}
            >
              Rename
            </button>
            {onArchive && (
              <button
                type="button"
                onClick={() => onArchive(r.id, !r.archived)}
                className="text-[10px] text-amber-700 hover:underline"
                data-testid={`run-archive-${r.id}`}
              >
                {r.archived ? "Restore" : "Archive"}
              </button>
            )}
            {onRecrawl && !r.archived && (
              <button
                type="button"
                onClick={() => onRecrawl(r.id, r.run_name)}
                disabled={recrawlingRunId === r.id}
                className="text-[10px] text-indigo-600 hover:underline disabled:text-zinc-400 disabled:cursor-not-allowed"
                data-testid={`run-recrawl-${r.id}`}
                title="Re-crawl every lead in this run"
              >
                {recrawlingRunId === r.id ? "Re-crawling…" : "Re-crawl"}
              </button>
            )}
            <button
              type="button"
              onClick={() => onConfirmDelete({ run: r, cascade: false })}
              className="text-[10px] text-rose-600 hover:underline"
              data-testid={`run-delete-${r.id}`}
            >
              Delete
            </button>
          </div>
        )}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap mt-2">
        <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200" data-testid={`run-count-pending-${r.id}`}>
          {r.pending_count ?? 0} pending
        </Badge>
        <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`run-count-saved-${r.id}`}>
          {r.saved_count ?? 0} saved
        </Badge>
        <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200" data-testid={`run-count-rejected-${r.id}`}>
          {r.rejected_count ?? 0} rejected
        </Badge>
        {(r.needs_review_count ?? 0) > 0 && (
          <Badge variant="outline" className="text-[10px] bg-orange-50 text-orange-700 border-orange-200" data-testid={`run-count-review-${r.id}`}>
            {r.needs_review_count} needs review
          </Badge>
        )}
      </div>
    </div>
  );
}

function RunHistorySheet({ open, onClose, tokenHeader, API, onRename, onDelete, onArchive, onRecrawl, recrawlingRunId, activeRunId, setActiveRunId }) {
  const [allRuns, setAllRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("active"); // "active" | "archived"
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchRuns = async () => {
    if (!open) return;
    setLoading(true);
    try {
      const params = {
        limit: 200,
        sort_by: sortBy,
        sort_dir: sortDir,
      };
      if (view === "archived") params.archived = true;
      if (search.trim()) params.search = search.trim();
      const r = await axios.get(`${API}/lead-intel/runs`, { headers: tokenHeader(), params });
      setAllRuns(r.data.runs || []);
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Admin access required.");
      else toast.error("Failed to load run history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchRuns();
    /* eslint-disable-next-line */
  }, [open, view, sortBy, sortDir]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(fetchRuns, 300);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [search]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 z-40 flex justify-end"
      onClick={onClose}
      data-testid="run-history-overlay"
    >
      <div
        className="bg-white w-full max-w-4xl h-full overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="run-history-sheet"
      >
        <div className="sticky top-0 bg-white border-b border-zinc-100 px-6 py-4 z-10">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-lg font-semibold text-zinc-900">Run History</h2>
              <p className="text-xs text-zinc-500 mt-0.5">All discovery runs, including archived</p>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="run-history-close">
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-lg border border-zinc-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setView("active")}
                className={`text-xs px-3 py-1.5 ${view === "active" ? "bg-indigo-600 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
                data-testid="history-view-active"
              >
                Active
              </button>
              <button
                type="button"
                onClick={() => setView("archived")}
                className={`text-xs px-3 py-1.5 border-l border-zinc-200 ${view === "archived" ? "bg-indigo-600 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
                data-testid="history-view-archived"
              >
                Archived
              </button>
            </div>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, category, city…"
              className="h-8 text-xs flex-1 min-w-[200px]"
              data-testid="history-search"
            />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="h-8 text-xs border border-zinc-200 rounded-md px-2 bg-white"
              data-testid="history-sort-by"
            >
              <option value="created_at">Date</option>
              <option value="category">Category</option>
              <option value="city">Location</option>
              <option value="pending_count">Pending count</option>
              <option value="opportunity_score">Opportunity score</option>
            </select>
            <button
              type="button"
              onClick={() => setSortDir(sortDir === "desc" ? "asc" : "desc")}
              className="h-8 text-xs border border-zinc-200 rounded-md px-2 bg-white hover:bg-zinc-50"
              data-testid="history-sort-dir"
              title={sortDir === "desc" ? "Descending" : "Ascending"}
            >
              {sortDir === "desc" ? "↓" : "↑"}
            </button>
          </div>
        </div>

        <div className="px-6 py-4">
          {loading && <div className="text-center text-sm text-zinc-500 py-8">Loading…</div>}
          {!loading && allRuns.length === 0 && (
            <div className="text-center text-sm text-zinc-500 py-12" data-testid="history-empty">
              {view === "archived" ? "No archived runs yet." : "No runs match your filters."}
            </div>
          )}
          {!loading && allRuns.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="history-grid">
              {allRuns.map(r => (
                <RunCard
                  key={r.id}
                  run={r}
                  isActive={activeRunId === r.id}
                  isEditing={editingId === r.id}
                  editName={editName}
                  setEditName={setEditName}
                  setEditingId={setEditingId}
                  onActivate={() => setActiveRunId(r.id)}
                  onRename={onRename}
                  onArchive={onArchive}
                  onRecrawl={onRecrawl}
                  recrawlingRunId={recrawlingRunId}
                  onConfirmDelete={setConfirmDelete}
                />
              ))}
            </div>
          )}
        </div>

        {confirmDelete && (
          <div
            className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
            onClick={() => setConfirmDelete(null)}
            data-testid="history-delete-modal-overlay"
          >
            <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <CardHeader>
                <CardTitle className="text-base">Delete discovery run?</CardTitle>
                <p className="text-xs text-zinc-500">
                  {confirmDelete.run.run_name || `${confirmDelete.run.category} · ${confirmDelete.run.city}`}
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-zinc-700">
                  By default we'll only delete the run record. The {confirmDelete.run.total_leads ?? 0} lead{confirmDelete.run.total_leads === 1 ? "" : "s"} discovered during this run will stay in your database.
                </p>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={confirmDelete.cascade}
                    onChange={(e) => setConfirmDelete({ ...confirmDelete, cascade: e.target.checked })}
                    className="mt-1"
                  />
                  <span>
                    Also delete all <b>{confirmDelete.run.total_leads ?? 0}</b> lead{confirmDelete.run.total_leads === 1 ? "" : "s"} from this run (irreversible).
                  </span>
                </label>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="ghost" onClick={() => setConfirmDelete(null)}>Cancel</Button>
                  <Button
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                    onClick={() => {
                      onDelete(confirmDelete.run.id, confirmDelete.cascade);
                      setAllRuns(prev => prev.filter(x => x.id !== confirmDelete.run.id));
                      setConfirmDelete(null);
                    }}
                    data-testid="history-delete-confirm"
                  >
                    Delete{confirmDelete.cascade ? " run + leads" : " run"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}


function BulkActionBar({
  leads, selectedIds, setSelectedIds,
  bucket, bucketCounts, activeRunId,
  filters, searchQ, onlyNeedsReview,
  rejectionReasons, tokenHeader, API, onDone,
  onRecrawlSelected, onBulkSend, recrawlingId,
}) {
  const [confirm, setConfirm] = useState(null);
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false);
  // confirm = { action: "save"|"reject"|"delete", mode: "selected"|"all-pending", count, payload }
  const [reason, setReason] = useState(rejectionReasons[0] || "Wrong category");
  const [reasonDetail, setReasonDetail] = useState("");
  const [working, setWorking] = useState(false);

  if (!leads.length && !selectedIds.size) return null;

  const allVisibleIds = leads.map(l => l.id);
  const allChecked = leads.length > 0 && allVisibleIds.every(id => selectedIds.has(id));
  const someChecked = !allChecked && allVisibleIds.some(id => selectedIds.has(id));

  const toggleAll = () => {
    if (allChecked) setSelectedIds(new Set());
    else setSelectedIds(new Set(allVisibleIds));
  };

  // Build the scope payload that the backend can use to apply an action to
  // ALL leads matching the current filters (not just the ones rendered).
  const buildScope = (forceStatus) => {
    const scope = { ...filters };
    if (searchQ.trim()) scope.search = searchQ.trim();
    scope.review_status = forceStatus || bucket;
    if (bucket === "pending" && onlyNeedsReview) scope.needs_review = true;
    if (activeRunId) scope.discovery_run_id = activeRunId;
    return scope;
  };


  const moveBulkTo = async (bucketValue) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    let modified = 0;
    // Step 1: the actual move — surface real server errors only.
    try {
      const r = await axios.post(
        `${API}/lead-intel/leads/bulk`,
        { action: "set_bucket", lead_ids: ids, bucket: bucketValue },
        { headers: tokenHeader() }
      );
      modified = r?.data?.modified ?? ids.length;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? `Move failed — ${detail[0]?.msg || JSON.stringify(detail[0])}`
        : (typeof detail === "string" ? detail : `Move failed (HTTP ${e?.response?.status || "?"})`);
      toast.error(msg);
      return;
    }
    // Step 2: success — always show success toast, refresh in the background.
    const labels = {
      pending: "Pending Review",
      pending_no_email: "Pending — No Email",
      saved: "Saved",
      rejected: "Rejected",
    };
    toast.success(`Moved ${modified} lead${modified === 1 ? "" : "s"} to ${labels[bucketValue] || bucketValue}.`);
    setSelectedIds(new Set());
    // Refresh without letting a refresh error masquerade as a move failure.
    try { await fetchLeads(); } catch (e) { /* refresh fail is non-fatal */ }
  };

  const startConfirm = async (action, mode) => {
    let count = 0;
    let payload = { action };
    if (action === "reject") {
      payload.rejection_reason = reason;
      if (reasonDetail.trim()) payload.rejection_detail = reasonDetail.trim();
    }
    if (mode === "selected") {
      const ids = Array.from(selectedIds);
      count = ids.length;
      payload.lead_ids = ids;
    } else {
      // all-pending: scope-based; force review_status=pending so we never
      // touch saved/rejected leads regardless of which bucket the user is on
      payload.scope = buildScope("pending");
      count = bucketCounts.pending ?? 0;
    }
    setConfirm({ action, mode, count, payload });
  };

  const runBulk = async () => {
    if (!confirm) return;
    setWorking(true);
    try {
      const r = await axios.post(`${API}/lead-intel/leads/bulk`, confirm.payload, { headers: tokenHeader() });
      toast.success(
        `${confirm.action === "delete" ? "Deleted" : confirm.action === "save" ? "Saved" : "Rejected"} ${r.data.modified ?? r.data.matched} lead${(r.data.modified ?? r.data.matched) === 1 ? "" : "s"}`,
      );
      setConfirm(null);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk action failed");
    } finally {
      setWorking(false);
    }
  };

  const selCount = selectedIds.size;
  const pendingCount = bucketCounts.pending ?? 0;

  return (
    <>
      <div
        className="flex items-center gap-3 px-4 py-2 bg-zinc-50 border border-zinc-100 rounded-xl text-xs"
        data-testid="bulk-action-bar"
      >
        {/* Select-all checkbox */}
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={allChecked}
            ref={el => { if (el) el.indeterminate = someChecked; }}
            onChange={toggleAll}
            data-testid="bulk-select-all"
            className="w-4 h-4 accent-indigo-600"
          />
          <span className="text-zinc-600">
            {selCount > 0
              ? <><b>{selCount}</b> selected</>
              : "Select all on this view"}
          </span>
        </label>

        {selCount > 0 && (
          <>
            <span className="text-zinc-300">|</span>
            <Button
              size="sm" variant="outline" className="h-7 text-xs"
              onClick={() => startConfirm("save", "selected")}
              data-testid="bulk-save"
            >
              <Save className="w-3 h-3 mr-1" /> Save selected
            </Button>
            <Button
              size="sm" variant="outline" className="h-7 text-xs"
              onClick={() => startConfirm("reject", "selected")}
              data-testid="bulk-reject"
            >
              <XCircle className="w-3 h-3 mr-1" /> Reject selected
            </Button>
            <Button
              size="sm" variant="outline" className="h-7 text-xs text-indigo-700 border-indigo-200 hover:bg-indigo-50"
              onClick={onBulkSend}
              data-testid="bulk-send-email"
              title="Send outreach emails to every selected lead"
            >
              <Send className="w-3 h-3 mr-1" /> Send emails ({selCount})
            </Button>
            <Button
              size="sm" variant="outline" className="h-7 text-xs text-rose-700 border-rose-200 hover:bg-rose-50"
              onClick={() => startConfirm("delete", "selected")}
              data-testid="bulk-delete"
            >
              <Trash2 className="w-3 h-3 mr-1" /> Delete selected
            </Button>
            <Button
              size="sm" variant="outline" className="h-7 text-xs"
              onClick={onRecrawlSelected}
              disabled={!!recrawlingId}
              data-testid="bulk-recrawl"
              title="Re-fetch website + recompute scores for every selected lead"
            >
              <RefreshCw className={`w-3 h-3 mr-1 ${recrawlingId === "bulk" ? "animate-spin" : ""}`} />
              {recrawlingId === "bulk" ? "Re-crawling…" : "Re-crawl selected"}
            </Button>
            {/* Move to ▾ — change the workflow bucket of every selected lead.
                Targets are filtered so we never offer "move to the bucket
                you're already on". */}
            <div className="relative inline-block" data-testid="bulk-move-to-wrap">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setBulkMoveOpen(v => !v);
                }}
                className="h-7 px-3 text-xs inline-flex items-center gap-1 rounded-md border border-zinc-200 bg-white hover:bg-zinc-50"
                data-testid="bulk-move-to-btn"
              >
                <ArrowRightLeft className="w-3 h-3" /> Move to ▾
              </button>
              {bulkMoveOpen && (
                <div className="absolute right-0 bottom-full mb-1 z-50 bg-white border border-zinc-200 rounded-md shadow-lg py-1 min-w-[180px]" data-testid="bulk-move-to-menu">
                  {[
                    { value: "pending",          label: "Pending Review",    tone: "text-indigo-700" },
                    { value: "pending_no_email", label: "Pending — No Email", tone: "text-amber-700" },
                    { value: "saved",            label: "Saved",             tone: "text-emerald-700" },
                    { value: "rejected",         label: "Rejected",          tone: "text-rose-700" },
                  ]
                    .filter(o => o.value !== bucket)
                    .map(o => (
                      <button
                        key={o.value}
                        type="button"
                        onClick={() => {
                          setBulkMoveOpen(false);
                          if (o.value === "rejected") {
                            startConfirm("reject", "selected");
                          } else {
                            moveBulkTo(o.value);
                          }
                        }}
                        data-testid={`bulk-move-to-${o.value}`}
                        className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-zinc-50 ${o.tone}`}
                      >
                        {o.label}
                      </button>
                    ))}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="text-[11px] text-zinc-500 hover:underline ml-auto"
              data-testid="bulk-clear"
            >
              Clear
            </button>
          </>
        )}

        {/* Save-all / Reject-all Pending — visible only when reviewing the
            Pending bucket so the action target is unambiguous */}
        {selCount === 0 && bucket === "pending" && pendingCount > 0 && (
          <>
            <span className="text-zinc-300 ml-auto">|</span>
            <Button
              size="sm" variant="ghost" className="h-7 text-xs text-zinc-700 hover:bg-zinc-100"
              onClick={() => {
                // Smart Triage = pre-select likely rejects, user reviews + confirms
                const ids = new Set(
                  leads
                    .filter(l =>
                      l.identity_confidence === "unable_to_verify"
                      || (l.is_franchise_chain && !l.location_specific_verified)
                      || l.website_reachable === false
                    )
                    .map(l => l.id),
                );
                if (ids.size === 0) {
                  toast.info("Smart Triage found no likely rejects in this view.");
                  return;
                }
                setSelectedIds(ids);
                toast.success(`Smart Triage pre-selected ${ids.size} likely reject${ids.size === 1 ? "" : "s"} — review and reject when ready.`);
              }}
              data-testid="bulk-smart-triage"
              title="Pre-select leads with unverifiable identity, generic franchises without local addresses, or unreachable websites"
            >
              <Sparkles className="w-3 h-3 mr-1 text-indigo-500" /> Smart Triage
            </Button>
            <Button
              size="sm" variant="ghost" className="h-7 text-xs text-emerald-700 hover:bg-emerald-50"
              onClick={() => startConfirm("save", "all-pending")}
              data-testid="bulk-save-all-pending"
            >
              Save all {pendingCount} pending
            </Button>
            <Button
              size="sm" variant="ghost" className="h-7 text-xs text-rose-700 hover:bg-rose-50"
              onClick={() => startConfirm("reject", "all-pending")}
              data-testid="bulk-reject-all-pending"
            >
              Reject all {pendingCount} pending
            </Button>
          </>
        )}
      </div>

      {/* Confirmation modal */}
      {confirm && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => !working && setConfirm(null)}
          data-testid="bulk-confirm-overlay"
        >
          <Card className="w-full max-w-md" onClick={e => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-base capitalize">
                {confirm.action} {confirm.count} lead{confirm.count === 1 ? "" : "s"}?
              </CardTitle>
              <p className="text-xs text-zinc-500">
                {confirm.mode === "all-pending"
                  ? <>This will {confirm.action} <b>every Pending lead</b> in {activeRunId ? "the current run" : "the current filtered view"}.</>
                  : <>This will {confirm.action} the {confirm.count} lead{confirm.count === 1 ? "" : "s"} you've selected.</>
                }
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              {confirm.action === "reject" && (
                <div className="space-y-2">
                  <Label className="text-xs">Rejection reason (shared)</Label>
                  <select
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    className="w-full h-9 border border-zinc-200 rounded-md px-2 text-sm bg-white"
                    data-testid="bulk-reject-reason"
                  >
                    {rejectionReasons.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <Label className="text-xs">Detail (optional)</Label>
                  <Input
                    value={reasonDetail}
                    onChange={e => setReasonDetail(e.target.value)}
                    placeholder="Optional additional context…"
                    className="h-9 text-sm"
                    data-testid="bulk-reject-detail"
                  />
                </div>
              )}
              {confirm.action === "delete" && (
                <p className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-md px-3 py-2">
                  Permanent delete. Re-running discovery may re-create these leads.
                </p>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" disabled={working} onClick={() => setConfirm(null)} data-testid="bulk-confirm-cancel">
                  Cancel
                </Button>
                <Button
                  disabled={working}
                  className={`${
                    confirm.action === "delete" ? "bg-rose-600 hover:bg-rose-700" :
                    confirm.action === "reject" ? "bg-amber-600 hover:bg-amber-700" :
                    "bg-emerald-600 hover:bg-emerald-700"
                  } text-white`}
                  onClick={runBulk}
                  data-testid="bulk-confirm-go"
                >
                  {working ? "Working…" : `Confirm ${confirm.action}`}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
